import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useId,
  type JSX,
  type ReactNode,
  type MutableRefObject,
  type ChangeEvent,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  Building2,
  Tag,
  FolderTree,
  Globe,
  Check,
  User,
  Pencil,
  Save,
  X,
  Plus,
  Trash2,
  Star,
  Hammer,
  Warehouse,
  ChevronDown,
  ChevronRight,
  GitMerge,
  Upload,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../../hooks/useApp';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import { supabase } from '../../lib/supabase';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { FormModal } from '../../components/ui/FormModal';
import ArbiSelect from '../../components/ui/ArbiSelect';
import NotesPanel from '../../components/notes/NotesPanel';
import { Toast, type ToastState } from '../../components/ui/Toast';
import { ProvenanceTooltip } from '../../components/ui/ProvenanceTooltip';
import type { ProvenanceMap } from '../../components/ui/ProvenanceIndicator';
import { useParamStore } from '../../hooks/useParamStore';
import type { Distributor } from '../../types';
import { extractEnvelope } from '../../lib/extractEnvelope';
import { translatePostgrestError } from '../../lib/translatePostgrestError';
import { FieldError } from '../../components/ui/FieldError';

// Loose RPC caller for get_credit_summaries (a held-batch fn not yet in the generated
// types until the Push-1 regen); read .data directly.
type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpcLoose = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;
import type { UserError } from '../../lib/errorCodes';
import {
  PROVENANCE_SOURCE_DIRECTORY_CREATE,
  PROVENANCE_SOURCE_DIRECTORY_EDIT,
} from '../../lib/provenanceSources';
import * as XLSX from 'xlsx';
import {
  resolveMappingRows,
  normalizeKey,
  type RawMappingRow,
  type ResolveResult,
} from '../../lib/categoryMappingImport';

// ── Local types ───────────────────────────────────────────────────────────

type SectionId =
  | 'distributors'
  | 'manufacturers'
  | 'warehouses'
  | 'brands'
  | 'categories'
  | 'marketplaces';

interface ManufacturerRow {
  id: string;
  name: string;
  is_active: boolean;
  // Day 83 P6.2 — per-field provenance JSONB sidecar (Day 82 L1 envelope).
  provenance?: ProvenanceMap | null;
}

interface ManufacturerDistributorRow {
  id: string;
  manufacturer_id: string;
  distributor_id: string;
  discount_pct: number | string;
  // W1 — buy-side import markup % (base -> cost_usd at ingestion); engine-inert.
  markup_pct: number | string | null;
}

interface SectionProps {
  sectionRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  showToast: (t: ToastState) => void;
  isAdmin: boolean;
  // Day 83 P6.2 — manual_override_stale_days threshold passed from parent
  // (resolved once via useParamStore in the top-level Directory component).
  // Consumed by ProvenanceTooltip wraps in each section's card view.
  staleDays: number;
}

interface DistributorContactRow {
  id: string;
  distributor_id: string;
  name: string | null;
  role: string | null;
  email: string | null;
  phone: string | null;
  is_primary: boolean | null;
}

interface BrandRow {
  id: string;
  name: string | null;
  gating_status?: string | null;
  restocking_fee_pct?: number | null;
  marketplace_permissions?: Record<string, boolean> | null;
  is_active?: boolean | null;
  manufacturer_id?: string | null;
  auto_seeded?: boolean | null;
  enforces_map?: boolean | null;
  // Day 83 P6.2 — per-field provenance JSONB sidecar (Day 82 L1 envelope).
  provenance?: ProvenanceMap | null;
}

interface CategoryRow {
  id: string;
  name: string | null;
  parent_id?: string | null;
  is_active?: boolean | null;
}

interface MarketplaceRow {
  id: string;
  channel: string | null;
  display_name: string | null;
  api_connection_status?: string | null;
  seller_id?: string | null;
  seller_account_name?: string | null;
  is_active?: boolean | null;
  referral_fee_pct?: number | null;
  fba_fee_per_unit?: number | null;
  wfs_fee_per_unit?: number | null;
  payout_cycle_days?: number | null;
  listing_fee?: number | null;
  currency?: string | null;
}

interface WarehouseRow {
  id: string;
  name: string | null;
  code: string | null;
  address?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  is_active?: boolean | null;
}

// ── Formatting ────────────────────────────────────────────────────────────

function fmt$(v: number | string | null | undefined): string {
  if (v == null || v === '') return '\u2014';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Nav structure ─────────────────────────────────────────────────────────

interface NavItem {
  id: SectionId;
  label: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'distributors', label: 'Distributors', icon: Building2 },
  { id: 'manufacturers', label: 'Manufacturers', icon: Hammer },
  { id: 'warehouses', label: 'Warehouses', icon: Warehouse },
  { id: 'brands', label: 'Brands', icon: Tag },
  { id: 'categories', label: 'Categories', icon: FolderTree },
  { id: 'marketplaces', label: 'Marketplaces', icon: Globe },
];

// ── Section Card wrapper ──────────────────────────────────────────────────

interface SectionCardProps {
  id: SectionId;
  title: string;
  action?: ReactNode;
  children: ReactNode;
  sectionRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
}

function SectionCard({ id, title, action, children, sectionRefs }: SectionCardProps): JSX.Element {
  return (
    <div
      id={`section-${id}`}
      ref={(el) => {
        if (sectionRefs) sectionRefs.current[id] = el;
      }}
      className="bg-surface-1 border border-border rounded-lg"
    >
      <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink-bright">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// ── Distributors section ──────────────────────────────────────────────────

/** Local draft shape while a distributor row is being edited. */
interface ContactDraft {
  /** Stable client-side key — id for existing rows, random uuid for new. */
  _key: string;
  /** Undefined for contacts that haven't been persisted yet. */
  id?: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  is_primary: boolean;
}

interface EditDraft {
  name: string;
  code: string;
  email: string;
  credit_limit: string;
  payment_terms: string;
  /** Percentage form ("8.25" = 8.25%); empty string = NULL on DB. Stored as
   *  decimal (0.0825) on distributors.estimated_tax_pct via /100 on save. */
  estimated_tax_pct: string;
  /** Day 85 (P7.1) — operator-policy flag. When true, satisfies
   *  setup_complete gate alongside estimated_tax_pct (either suffices).
   *  Set true when distributor accepts resale certificate. */
  tax_exempt: boolean;
  /** Day 90 (CF 60) — per-channel permission policy at distributor tier
   *  (ADR 46). Shape: Record<marketplaces.channel, boolean>. Empty `{}` =
   *  no opinion (defers to default allow). Edited via picker in
   *  DistributorCardEdit body (A9); omitted from DistributorsAddForm
   *  per A9 omit-at-Add. */
  marketplace_permissions: Record<string, boolean>;
  contacts: ContactDraft[];
  /** Server-side ids of contacts the user removed — delete on save. */
  deletedContactIds: string[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildDraft(d: Distributor, contacts: DistributorContactRow[]): EditDraft {
  return {
    name: d.name ?? '',
    code: d.code ?? '',
    email: d.email ?? '',
    credit_limit: d.credit_limit != null ? String(d.credit_limit) : '',
    payment_terms: d.payment_terms != null ? String(d.payment_terms) : '',
    estimated_tax_pct: d.estimated_tax_pct != null ? String(Number(d.estimated_tax_pct) * 100) : '',
    tax_exempt: !!d.tax_exempt,
    // Day 90 (CF 60) — coerce null/missing column → {} (no opinion) per
    // BrandForm L2900 brandToDraft precedent. Operator picks per-channel
    // explicit allow/deny via picker; missing key defers to default.
    marketplace_permissions:
      (d.marketplace_permissions as Record<string, boolean> | null | undefined) ?? {},
    contacts: contacts.map((c) => ({
      _key: c.id,
      id: c.id,
      name: c.name ?? '',
      role: c.role ?? '',
      email: c.email ?? '',
      phone: c.phone ?? '',
      is_primary: !!c.is_primary,
    })),
    deletedContactIds: [],
  };
}

/** D.14 — empty draft for the Add form. Parallel to buildDraft but seeds
 *  fresh defaults. Contacts array starts empty per M6 (operator hits
 *  "Add Contact" to begin); deletedContactIds carried for shape parity. */
function buildEmptyDraft(): EditDraft {
  return {
    name: '',
    code: '',
    email: '',
    credit_limit: '',
    payment_terms: '',
    estimated_tax_pct: '',
    tax_exempt: false,
    // Day 90 (CF 60) — type-parity init only; DistributorsAddForm omits the
    // picker per A9 omit-at-Add. handleAdd payload does NOT include
    // marketplace_permissions field (new distributors land with column NULL).
    marketplace_permissions: {},
    contacts: [],
    deletedContactIds: [],
  };
}

/** Per-card edit form. Kept as a dedicated component so each card owns its
 *  own draft state — entering edit on one card never disturbs another. */
interface DistributorCardEditProps {
  open: boolean;
  distributor: Distributor;
  contacts: DistributorContactRow[];
  onCancel: () => void;
  onSaved: () => void;
  showToast: (t: ToastState) => void;
  creditUsed: number;
}

function DistributorCardEdit({
  open,
  distributor,
  contacts,
  onCancel,
  onSaved,
  showToast,
  creditUsed,
}: DistributorCardEditProps): JSX.Element {
  // Day 90 (CF 60) — marketplaces for Marketplace Permissions picker per A9.
  // useSettingsConfig().marketplaces loads from cache (60s TTL per Day 60a
  // Decision 35); no separate query per Edit mode entry.
  const { marketplaces } = useSettingsConfig();
  const [draft, setDraft] = useState<EditDraft>(() => buildDraft(distributor, contacts));
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Day 84.6 P5 — server-side field errors from translatePostgrestError. Parallel
  // to client-validation `errors` state per Option C; cleared on each save attempt
  // pre-RPC so the two-state collision is timing-isolated.
  const [serverFieldErrors, setServerFieldErrors] = useState<UserError[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmRemoveKey, setConfirmRemoveKey] = useState<string | null>(null);
  // Day 85 P4 — true → false setup_complete transition guard per Adjudication
  // C. Set when save would clear gate fields on a setup-complete row; cleared
  // on confirm (handleSave re-fires with skipRevertCheck=true) or cancel.
  const [showRevertConfirm, setShowRevertConfirm] = useState<boolean>(false);
  // Day 83 P6.2 — reason capture per L5 multi-field surface pattern. Single
  // textarea applies to all changed tracked fields in this save bundle per L6
  // shared-reason policy.
  const [reason, setReason] = useState<string>('');

  function updateField<K extends keyof EditDraft>(key: K, val: EditDraft[K]): void {
    setDraft((prev) => ({ ...prev, [key]: val }));
  }

  // Day 90 (CF 60) — togglePerm helper. Mirrors BrandForm Directory.tsx L2959
  // verbatim shape. Toggles boolean for a single channel; missing key = no
  // opinion. Admin gating cascades indirectly via DistributorCardView Edit
  // affordance per G7 informational finding (no inline isAdmin check needed).
  function togglePerm(channel: string): void {
    setDraft((prev) => {
      const next = { ...prev.marketplace_permissions };
      if (next[channel]) {
        delete next[channel];
      } else {
        next[channel] = true;
      }
      return { ...prev, marketplace_permissions: next };
    });
  }

  function updateContact(key: string, patch: Partial<ContactDraft>): void {
    setDraft((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => (c._key === key ? { ...c, ...patch } : c)),
    }));
  }

  function addContact(): void {
    setDraft((prev) => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          _key: crypto.randomUUID(),
          name: '',
          role: '',
          email: '',
          phone: '',
          is_primary: false,
        },
      ],
    }));
  }

  function confirmRemoveContact(key: string): void {
    setDraft((prev) => {
      const target = prev.contacts.find((c) => c._key === key);
      const deleted = target?.id ? [...prev.deletedContactIds, target.id] : prev.deletedContactIds;
      return {
        ...prev,
        contacts: prev.contacts.filter((c) => c._key !== key),
        deletedContactIds: deleted,
      };
    });
    setConfirmRemoveKey(null);
  }

  function setPrimary(key: string): void {
    // Only one contact can be primary. Toggling one sets the others to false.
    setDraft((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => ({ ...c, is_primary: c._key === key })),
    }));
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!draft.name.trim()) e.name = 'Company name is required';
    if (!draft.code.trim()) e.code = 'Distributor code is required';
    if (draft.email.trim() && !EMAIL_RE.test(draft.email.trim()))
      e.email = 'Enter a valid email address';
    if (draft.credit_limit.trim()) {
      const n = Number(draft.credit_limit);
      if (!Number.isFinite(n) || n < 0) e.credit_limit = 'Credit limit must be a positive number';
    }
    if (draft.payment_terms.trim()) {
      const n = Number(draft.payment_terms);
      if (!Number.isFinite(n) || n < 0)
        e.payment_terms = 'Payment terms must be a positive number of days';
    }
    if (draft.estimated_tax_pct.trim()) {
      const n = Number(draft.estimated_tax_pct);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        e.estimated_tax_pct = 'Tax % must be between 0 and 100';
      }
    }
    draft.contacts.forEach((c, i) => {
      if (!c.name.trim()) e[`contact_${i}_name`] = 'Contact name is required';
      if (c.email.trim() && !EMAIL_RE.test(c.email.trim()))
        e[`contact_${i}_email`] = 'Enter a valid email address';
    });
    return e;
  }

  async function handleSave(skipRevertCheck = false): Promise<void> {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    // Day 85 P4 — true → false setup_complete guard per Adjudication C.
    // Predict trigger gate from form draft: name + code populated AND
    // (estimated_tax_pct populated OR tax_exempt checked). If currently
    // setup-complete and predicted incomplete, intercept with ConfirmDialog.
    if (!skipRevertCheck && distributor.setup_complete) {
      const predictedComplete =
        draft.name.trim() !== '' &&
        draft.code.trim() !== '' &&
        (draft.estimated_tax_pct.trim() !== '' || draft.tax_exempt);
      if (!predictedComplete) {
        setShowRevertConfirm(true);
        return;
      }
    }
    setSaving(true);
    setServerFieldErrors([]);
    try {
      // Day 83 P6.2 — smart-spread provenance pattern (drift #13 resolution).
      // UPDATE column-replace semantics would wipe unchanged-field envelopes
      // if we only supplied changed-field keys; spread prior provenance first
      // to preserve them, then overlay changed-field envelopes with new
      // source + reason. Trigger gates on IS DISTINCT FROM so unchanged-field
      // envelopes pass through untouched (preserving their 5-key historical
      // shape from prior writes).
      const newCreditLimit = draft.credit_limit.trim() === '' ? null : Number(draft.credit_limit);
      const newPaymentTerms =
        draft.payment_terms.trim() === '' ? null : Number(draft.payment_terms);
      const changedCreditLimit = newCreditLimit !== (distributor.credit_limit ?? null);
      const changedPaymentTerms = newPaymentTerms !== (distributor.payment_terms ?? null);
      // Day 90 (CF 60) — marketplace_permissions change detection via JSON
      // shape comparison (Record<channel, boolean> is order-stable for our
      // simple toggle-add/remove pattern; deep-equal would be over-engineered).
      // Empty {} vs null both mean "no opinion" — only fire provenance write
      // when stringified shape actually differs.
      const distMP =
        (distributor.marketplace_permissions as Record<string, boolean> | null | undefined) ?? {};
      const changedMarketplacePermissions =
        JSON.stringify(draft.marketplace_permissions) !== JSON.stringify(distMP);
      const reasonTrimmed = reason.trim() || null;
      const provenance: Record<string, { source: string; reason: string | null }> = {
        ...((distributor.provenance as Record<
          string,
          { source: string; reason: string | null }
        > | null) ?? {}),
      };
      if (changedCreditLimit) {
        provenance.credit_limit = {
          source: PROVENANCE_SOURCE_DIRECTORY_EDIT,
          reason: reasonTrimmed,
        };
      }
      if (changedPaymentTerms) {
        provenance.payment_terms = {
          source: PROVENANCE_SOURCE_DIRECTORY_EDIT,
          reason: reasonTrimmed,
        };
      }
      if (changedMarketplacePermissions) {
        // Day 90 (CF 60) — track marketplace_permissions in provenance per
        // R-Lock-5 (5th tracked field on provenance_distributors_capture
        // trigger from Phase 1A §3). App-side writes source + reason;
        // trigger writes previous_value + set_at + set_by per L5 defensive
        // split.
        provenance.marketplace_permissions = {
          source: PROVENANCE_SOURCE_DIRECTORY_EDIT,
          reason: reasonTrimmed,
        };
      }

      // U4 Slice D (R1B-F12) — money-config FE split. credit_limit + estimated_tax_pct are
      // REVOKEd from direct authenticated UPDATE; they route through the update_distributor_money
      // CAS RPC. Non-money fields + the full provenance envelope stay in this direct UPDATE,
      // which runs FIRST so provenance_distributors_capture enriches the credit_limit envelope
      // ({previous_value,set_at,set_by}) on the RPC's subsequent value change.
      const newEstimatedTaxPct =
        draft.estimated_tax_pct.trim() === '' ? null : Number(draft.estimated_tax_pct) / 100;
      // Scale-aware compare at the column's numeric(5,4) precision — avoids a floating-point
      // false-positive (…/100) blocking a non-money edit via a spurious money-CAS call.
      const taxScaled = (v: number | null): number | null =>
        v == null ? null : Math.round(v * 10000);
      const moneyChanged =
        changedCreditLimit ||
        taxScaled(newEstimatedTaxPct) !== taxScaled(distributor.estimated_tax_pct ?? null);

      // 1. Update distributor row — non-money columns + provenance (money cols excluded).
      const { error: distErr } = await supabase
        .from('distributors')
        .update({
          name: draft.name.trim(),
          code: draft.code.trim(),
          email: draft.email.trim() || null,
          payment_terms: newPaymentTerms,
          tax_exempt: draft.tax_exempt,
          // Day 90 (CF 60) — marketplace_permissions in UPDATE payload per A9.
          // Sent as Record<channel, boolean> JSON; trigger captures changes.
          marketplace_permissions: draft.marketplace_permissions,
          updated_at: new Date().toISOString(),
          provenance,
        } as never)
        .eq('id', distributor.id);
      if (distErr) {
        const ue = translatePostgrestError(distErr);
        if (ue.field_path !== null) {
          setServerFieldErrors([ue]);
          setSaving(false);
          return;
        }
        throw distErr;
      }

      // 1b. Money CAS — route credit_limit + estimated_tax_pct through the guarded RPC, only
      // when a money field actually changed. A stale expected value (a concurrent writer moved
      // it since load) returns STALE_CONFLICT → surface reload-and-retry, never a silent LWW.
      if (moneyChanged) {
        const moneyEnv = extractEnvelope<{ id: string }>(
          await supabase.rpc('update_distributor_money', {
            p_id: distributor.id,
            // Supabase types function args non-nullable; these columns are genuinely nullable
            // (no limit / tax-exempt) and the RPC's IS DISTINCT FROM CAS handles NULL — cast past
            // the type-gen limitation, the runtime NULL flows through as JSON null.
            p_expected_credit_limit: (distributor.credit_limit ?? null) as number,
            p_expected_estimated_tax_pct: (distributor.estimated_tax_pct ?? null) as number,
            p_new_credit_limit: newCreditLimit as number,
            p_new_estimated_tax_pct: newEstimatedTaxPct as number,
          })
        );
        if (!moneyEnv.isOk) {
          showToast({
            kind: 'error',
            message: moneyEnv.userErrors[0]?.message ?? 'Money update failed — reload and retry',
          });
          setSaving(false);
          return;
        }
      }

      // 2. Delete removed contacts
      for (const id of draft.deletedContactIds) {
        const { error } = await supabase.from('distributor_contacts').delete().eq('id', id);
        if (error) throw new Error(translatePostgrestError(error).message);
      }

      // 3. Upsert each contact — UPDATE when id exists, INSERT when it doesn't
      for (const c of draft.contacts) {
        const payload = {
          name: c.name.trim(),
          role: c.role.trim() || null,
          email: c.email.trim() || null,
          phone: c.phone.trim() || null,
          is_primary: c.is_primary,
        };
        if (c.id) {
          const { error } = await supabase
            .from('distributor_contacts')
            .update(payload as never)
            .eq('id', c.id);
          if (error) throw new Error(translatePostgrestError(error).message);
        } else {
          const { error } = await supabase
            .from('distributor_contacts')
            .insert({ ...payload, distributor_id: distributor.id } as never);
          if (error) throw new Error(translatePostgrestError(error).message);
        }
      }

      showToast({ kind: 'success', message: `${draft.name.trim()} updated` });
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast({ kind: 'error', message: `Save failed — ${msg}` });
    } finally {
      setSaving(false);
    }
  }

  const inputCls =
    'arbi-input w-full text-xs px-2 py-1 bg-surface-3 border border-border rounded focus:border-accent outline-none';
  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';
  const errCls = 'text-2xs text-danger mt-1';

  const confirmTargetContact = draft.contacts.find((c) => c._key === confirmRemoveKey);

  return (
    <>
      <FormModal
        open={open}
        title="Edit distributor"
        subtitle={distributor.name}
        size="3xl"
        saving={saving}
        confirmOpen={!!confirmRemoveKey || showRevertConfirm}
        onClose={onCancel}
        footer={
          <>
            <button
              onClick={onCancel}
              disabled={saving}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1 flex items-center gap-1"
            >
              <X size={12} /> Cancel
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 flex items-center gap-1"
            >
              <Save size={12} /> {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {/* Distributor-level fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor={`dist-${distributor.id}-name`} className={labelCls}>
              Company Name
            </label>
            <input
              id={`dist-${distributor.id}-name`}
              value={draft.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={inputCls}
              placeholder="Brooklyn Low Voltage Supply"
            />
            {errors.name && <p className={errCls}>{errors.name}</p>}
          </div>
          <div>
            <label htmlFor={`dist-${distributor.id}-code`} className={labelCls}>
              Distributor Code
            </label>
            <input
              id={`dist-${distributor.id}-code`}
              value={draft.code}
              onChange={(e) => updateField('code', e.target.value)}
              className={clsx(inputCls, 'font-mono uppercase')}
              placeholder="BLVS"
            />
            {errors.code && <p className={errCls}>{errors.code}</p>}
            <FieldError fieldPath="code" userErrors={serverFieldErrors} />
          </div>
          <div>
            <label htmlFor={`dist-${distributor.id}-email`} className={labelCls}>
              Business Email
            </label>
            <input
              id={`dist-${distributor.id}-email`}
              type="email"
              value={draft.email}
              onChange={(e) => updateField('email', e.target.value)}
              className={inputCls}
              placeholder="purchasing@example.com"
            />
            {errors.email && <p className={errCls}>{errors.email}</p>}
          </div>
          <div>
            <label htmlFor={`dist-${distributor.id}-credit`} className={labelCls}>
              Credit Limit (USD)
            </label>
            <input
              id={`dist-${distributor.id}-credit`}
              value={draft.credit_limit}
              onChange={(e) => updateField('credit_limit', e.target.value)}
              className={clsx(inputCls, 'font-mono')}
              placeholder="0.00"
              inputMode="decimal"
            />
            {errors.credit_limit && <p className={errCls}>{errors.credit_limit}</p>}
          </div>
          <div>
            <label htmlFor={`dist-${distributor.id}-terms`} className={labelCls}>
              Payment Terms (days)
            </label>
            <input
              id={`dist-${distributor.id}-terms`}
              value={draft.payment_terms}
              onChange={(e) => updateField('payment_terms', e.target.value)}
              className={clsx(inputCls, 'font-mono')}
              placeholder="30"
              inputMode="numeric"
            />
            {errors.payment_terms && <p className={errCls}>{errors.payment_terms}</p>}
            {/* Fix Pass A (S-F note) — payment_terms is recorded in provenance but
                not yet read by the profit/credit engine (days-credit-locked is
                unwired → post-launch). Labeled so it isn't mistaken for a live input. */}
            <p className="mt-1 text-2xs text-ink-muted">
              Recorded for reference; not yet used in calculations.
            </p>
          </div>
          <div>
            <label htmlFor={`dist-${distributor.id}-tax`} className={labelCls}>
              Estimated State Tax %
            </label>
            <input
              id={`dist-${distributor.id}-tax`}
              value={draft.estimated_tax_pct}
              onChange={(e) => updateField('estimated_tax_pct', e.target.value)}
              className={clsx(inputCls, 'font-mono')}
              placeholder="0.00"
              inputMode="decimal"
            />
            {errors.estimated_tax_pct && <p className={errCls}>{errors.estimated_tax_pct}</p>}
            <p className="text-2xs text-ink-muted mt-1">
              Estimate only — replaced by actual on invoice match. Leave blank if resale certificate
              is on file.
            </p>
            {/* Day 85 P2 — tax_exempt checkbox per Sol Checkpoint #2 C2-ii.
              Satisfies setup_complete gate alongside estimated_tax_pct
              (either suffices). No explicit provenance write; amended
              provenance_distributors_capture trigger captures defensively. */}
            <label className="flex items-center gap-2 mt-2 text-xs text-ink-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={draft.tax_exempt}
                onChange={(e) => updateField('tax_exempt', e.target.checked)}
                className="rounded border-border bg-surface-3 text-accent focus:ring-accent/30"
              />
              <span>Tax exempt (resale certificate)</span>
            </label>
            <p className="text-2xs text-ink-muted mt-0.5 ml-6">
              Check when this distributor accepts a resale certificate. Satisfies setup-complete
              without a tax % estimate.
            </p>
          </div>
          <div>
            <span className={labelCls}>Credit Used</span>
            <p className="arbi-input w-full text-xs px-2 py-1 bg-surface-3/40 border border-border-subtle rounded font-mono text-ink-muted">
              {fmt$(creditUsed)}
            </p>
            <p className="text-2xs text-ink-muted mt-1">
              Tracked by the credit ledger — not editable here
            </p>
          </div>
        </div>

        {/* Day 90 (CF 60) — Marketplace Permissions picker per A9. Section renders
          unconditionally inside Edit modal body (no inline isAdmin check); the
          admin gate cascades indirectly via DistributorCardView Edit affordance
          per G7 informational finding. Button-toggle JSX mirrors BrandForm
          L3015-L3043 verbatim shape. */}
        <div
          className="pt-3 border-t border-border-subtle"
          role="group"
          aria-label="Marketplace permissions"
        >
          <span className={labelCls}>Marketplace permissions</span>
          <p className="text-2xs text-ink-muted mt-0.5 mb-2">
            Per-channel policy at distributor tier. Explicit deny terminates the resolution chain.
            Missing key defers to default allow. Decision 46.
          </p>
          {marketplaces.length === 0 ? (
            <p className="text-2xs text-ink-muted">No marketplaces configured.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {marketplaces.map((m) => {
                const checked = !!draft.marketplace_permissions[m.channel];
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => togglePerm(m.channel)}
                    aria-pressed={checked}
                    className={clsx(
                      'text-2xs px-2 py-0.5 rounded border flex items-center gap-1',
                      checked
                        ? 'bg-accent/15 text-accent border-accent/40'
                        : 'bg-surface-3 text-ink-muted border-border-subtle'
                    )}
                  >
                    {checked ? <Check size={10} /> : null}
                    {m.display_name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Notes — replaces former scalar distributors.notes textarea */}
        <div className="pt-3 border-t border-border-subtle">
          <NotesPanel entityType="distributor" entityId={distributor.id} />
        </div>

        {/* Contacts editor */}
        <div className="pt-3 border-t border-border-subtle">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xs uppercase tracking-wider text-ink-muted">Contacts</span>
            <button
              onClick={addContact}
              className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
            >
              <Plus size={10} /> Add Contact
            </button>
          </div>
          {draft.contacts.length === 0 ? (
            <p className="text-xs text-ink-muted">No contacts added yet</p>
          ) : (
            <div className="space-y-3">
              {draft.contacts.map((c, i) => (
                <div
                  key={c._key}
                  className="bg-surface-3 border border-border-subtle rounded p-3 space-y-2"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor={`dist-${distributor.id}-c-${c._key}-name`}
                        className={labelCls}
                      >
                        Contact Name
                      </label>
                      <input
                        id={`dist-${distributor.id}-c-${c._key}-name`}
                        value={c.name}
                        onChange={(e) => updateContact(c._key, { name: e.target.value })}
                        className={inputCls}
                        placeholder="Full name"
                      />
                      {errors[`contact_${i}_name`] && (
                        <p className={errCls}>{errors[`contact_${i}_name`]}</p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor={`dist-${distributor.id}-c-${c._key}-role`}
                        className={labelCls}
                      >
                        Role / Title
                      </label>
                      <input
                        id={`dist-${distributor.id}-c-${c._key}-role`}
                        value={c.role}
                        onChange={(e) => updateContact(c._key, { role: e.target.value })}
                        className={inputCls}
                        placeholder="Purchasing manager"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`dist-${distributor.id}-c-${c._key}-email`}
                        className={labelCls}
                      >
                        Email
                      </label>
                      <input
                        id={`dist-${distributor.id}-c-${c._key}-email`}
                        type="email"
                        value={c.email}
                        onChange={(e) => updateContact(c._key, { email: e.target.value })}
                        className={inputCls}
                        placeholder="name@example.com"
                      />
                      {errors[`contact_${i}_email`] && (
                        <p className={errCls}>{errors[`contact_${i}_email`]}</p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor={`dist-${distributor.id}-c-${c._key}-phone`}
                        className={labelCls}
                      >
                        Phone
                      </label>
                      <input
                        id={`dist-${distributor.id}-c-${c._key}-phone`}
                        type="tel"
                        value={c.phone}
                        onChange={(e) => updateContact(c._key, { phone: e.target.value })}
                        className={inputCls}
                        placeholder="555-123-4567"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <button
                      onClick={() => setPrimary(c._key)}
                      aria-pressed={c.is_primary}
                      className={clsx(
                        'text-2xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors',
                        c.is_primary
                          ? 'bg-accent/15 text-accent'
                          : 'text-ink-muted hover:text-ink-secondary hover:bg-surface-4'
                      )}
                      title="Only one contact can be marked primary"
                    >
                      <Star size={10} fill={c.is_primary ? 'currentColor' : 'none'} />
                      {c.is_primary ? 'Primary Contact' : 'Set as Primary'}
                    </button>
                    <button
                      onClick={() =>
                        c.id ? setConfirmRemoveKey(c._key) : confirmRemoveContact(c._key)
                      }
                      className="text-2xs text-ink-muted hover:text-danger px-2 py-0.5 rounded flex items-center gap-1 transition-colors"
                      aria-label="Remove contact"
                    >
                      <Trash2 size={10} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Day 83 P6.2 — optional reason capture per L5 multi-field surface
          pattern. Single textarea applies to all changed tracked fields in
          this save bundle per L6 shared-reason policy. */}
        <div>
          <label htmlFor={`dist-${distributor.id}-reason`} className={labelCls}>
            Reason for change (optional)
          </label>
          <textarea
            id={`dist-${distributor.id}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputCls}
            rows={2}
            placeholder="Why this change? (optional)"
          />
        </div>
      </FormModal>

      {/* Remove-contact confirm — only shown for contacts that exist server-side
          since removing an unsaved contact draft is trivially reversible. */}
      <ConfirmDialog
        open={!!confirmRemoveKey}
        title="Remove Contact"
        message={
          confirmTargetContact
            ? `${confirmTargetContact.name || 'This contact'} will be removed from ${distributor.name} when you click Save. Cancel to keep.`
            : ''
        }
        onConfirm={() => confirmRemoveKey && confirmRemoveContact(confirmRemoveKey)}
        onCancel={() => setConfirmRemoveKey(null)}
      />
      {/* Day 85 P4 — setup_complete true → false revert confirmation per
          Adjudication C. Operator clearing both tax_pct and tax_exempt on a
          setup-complete distributor surfaces this guard. */}
      <ConfirmDialog
        open={showRevertConfirm}
        title="Mark setup incomplete?"
        message={`${distributor.name} is currently marked setup-complete. Clearing the tax % estimate (and not checking Tax exempt) will flag the distributor as setup-incomplete. Continue?`}
        confirmLabel="Mark incomplete"
        onConfirm={() => {
          setShowRevertConfirm(false);
          void handleSave(true);
        }}
        onCancel={() => setShowRevertConfirm(false)}
      />
    </>
  );
}

/** Read-only card view for a single distributor. Edit button renders when
 *  isAdmin is true. */
interface DistributorCardViewProps {
  distributor: Distributor;
  contacts: DistributorContactRow[];
  isAdmin: boolean;
  onEdit: () => void;
  staleDays: number;
  creditUsed: number;
}

function DistributorCardView({
  distributor,
  contacts,
  isAdmin,
  onEdit,
  staleDays,
  creditUsed,
}: DistributorCardViewProps): JSX.Element {
  const d = distributor;
  const distProv = (d.provenance as ProvenanceMap | null | undefined) ?? null;
  return (
    <div className="bg-surface-2 border border-border-subtle rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink-bright">{d.name}</span>
          <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-success/15 text-success">
            Active
          </span>
          {/* Day 85 P3 — setup_complete badge per Adjudication D-i (plain
              <span> with chip CSS; no ProvenanceTooltip wrap — derived/
              computed flag, not a tracked field per G8). Visible always
              (both states surfaced) to distinguish "explicitly complete"
              from "incomplete onboarding". */}
          {d.setup_complete ? (
            <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-success/15 text-success">
              Setup complete
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-warning/15 text-warning">
              Setup incomplete
            </span>
          )}
          {/* Day 85 P3 — tax_exempt chip per ProvenanceTooltip mode='chip'
              (tracked field per Sol C2-ii amendment). Visible only when
              true (operator-policy flag rendered when active; matches
              existing distributor-chip cluster convention). */}
          {d.tax_exempt && (
            <ProvenanceTooltip
              provenance={distProv}
              fieldKey="tax_exempt"
              staleThresholdDays={staleDays}
              mode="chip"
            >
              <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-accent/15 text-accent">
                Tax exempt
              </span>
            </ProvenanceTooltip>
          )}
        </div>
        {isAdmin && (
          <button
            onClick={onEdit}
            className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
            aria-label={`Edit ${d.name}`}
          >
            <Pencil size={10} /> Edit
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
        <div>
          <span className="text-ink-muted">Credit Limit</span>
          <p className="font-mono text-ink-primary">
            <ProvenanceTooltip
              provenance={distProv}
              fieldKey="credit_limit"
              staleThresholdDays={staleDays}
              mode="chip"
              previousValueFormatter={(v) => fmt$(typeof v === 'number' ? v : null)}
            >
              {fmt$(d.credit_limit)}
            </ProvenanceTooltip>
          </p>
        </div>
        <div>
          <span className="text-ink-muted">Credit Used</span>
          <p className="font-mono text-ink-primary">{fmt$(creditUsed)}</p>
        </div>
        <div>
          <span className="text-ink-muted">Payment Terms</span>
          <p className="font-mono text-ink-primary">
            <ProvenanceTooltip
              provenance={distProv}
              fieldKey="payment_terms"
              staleThresholdDays={staleDays}
              mode="chip"
            >
              {d.payment_terms != null ? `${d.payment_terms} days` : '\u2014'}
            </ProvenanceTooltip>
          </p>
        </div>
        <div>
          <span className="text-ink-muted">Code</span>
          <p className="font-mono text-ink-primary">{d.code || '\u2014'}</p>
        </div>
        {d.email && (
          <div className="col-span-2">
            <span className="text-ink-muted">Email</span>
            <p className="text-ink-primary">{d.email}</p>
          </div>
        )}
      </div>
      {/* Contacts */}
      <div className="mt-3 pt-3 border-t border-border-subtle">
        <div className="flex items-center justify-between mb-2">
          <span className="text-2xs uppercase tracking-wider text-ink-muted">Contacts</span>
        </div>
        {contacts.length === 0 ? (
          <p className="text-xs text-ink-muted">
            {isAdmin ? 'No contacts added yet — click Edit to add one.' : 'No contacts added yet'}
          </p>
        ) : (
          <div className="space-y-1.5">
            {contacts.map((c) => (
              <div key={c.id} className="flex items-center gap-3 text-xs flex-wrap">
                <User size={12} className="text-ink-muted flex-shrink-0" />
                <span className="text-ink-primary font-medium">{c.name}</span>
                {c.role && <span className="text-ink-muted">({c.role})</span>}
                {c.email && <span className="text-ink-secondary">{c.email}</span>}
                {c.phone && <span className="text-ink-secondary font-mono">{c.phone}</span>}
                {c.is_primary && (
                  <span
                    className="flex items-center gap-1 text-accent text-2xs"
                    title="Primary contact"
                  >
                    <Check size={12} /> Primary
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** D.14 — inline Add form for creating a new distributor. Mirrors
 *  DistributorCardEdit's form shape (contact-row machinery + helpers
 *  + validation) but performs an INSERT instead of UPDATE. No NotesPanel
 *  (no distributor.id pre-INSERT — M3); no setup_complete revert guard
 *  (Edit-only — Day 85 P4). Provenance envelope seeds 4 keys
 *  unconditionally per A6 with PROVENANCE_SOURCE_DIRECTORY_CREATE. */
interface DistributorsAddFormProps {
  open: boolean;
  onCancel: () => void;
  onAdded: () => Promise<void>;
  showToast: (t: ToastState) => void;
}

function DistributorsAddForm({
  open,
  onCancel,
  onAdded,
  showToast,
}: DistributorsAddFormProps): JSX.Element {
  const [draft, setDraft] = useState<EditDraft>(buildEmptyDraft);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverFieldErrors, setServerFieldErrors] = useState<UserError[]>([]);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState<string>('');

  function updateField<K extends keyof EditDraft>(key: K, val: EditDraft[K]): void {
    setDraft((prev) => ({ ...prev, [key]: val }));
  }

  function updateContact(key: string, patch: Partial<ContactDraft>): void {
    setDraft((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => (c._key === key ? { ...c, ...patch } : c)),
    }));
  }

  function addContact(): void {
    setDraft((prev) => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          _key: crypto.randomUUID(),
          name: '',
          role: '',
          email: '',
          phone: '',
          is_primary: false,
        },
      ],
    }));
  }

  // No ConfirmDialog needed — Add-form contacts have no server-side id, so
  // removal is trivially reversible (mirrors DistributorCardEdit
  // confirmRemoveContact's else-branch at L763).
  function removeContact(key: string): void {
    setDraft((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((c) => c._key !== key),
    }));
  }

  function setPrimary(key: string): void {
    setDraft((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => ({ ...c, is_primary: c._key === key })),
    }));
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!draft.name.trim()) e.name = 'Company name is required';
    if (!draft.code.trim()) e.code = 'Distributor code is required';
    if (draft.email.trim() && !EMAIL_RE.test(draft.email.trim()))
      e.email = 'Enter a valid email address';
    if (draft.credit_limit.trim()) {
      const n = Number(draft.credit_limit);
      if (!Number.isFinite(n) || n < 0) e.credit_limit = 'Credit limit must be a positive number';
    }
    if (draft.payment_terms.trim()) {
      const n = Number(draft.payment_terms);
      if (!Number.isFinite(n) || n < 0)
        e.payment_terms = 'Payment terms must be a positive number of days';
    }
    if (draft.estimated_tax_pct.trim()) {
      const n = Number(draft.estimated_tax_pct);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        e.estimated_tax_pct = 'Tax % must be between 0 and 100';
      }
    }
    draft.contacts.forEach((c, i) => {
      if (!c.name.trim()) e[`contact_${i}_name`] = 'Contact name is required';
      if (c.email.trim() && !EMAIL_RE.test(c.email.trim()))
        e[`contact_${i}_email`] = 'Enter a valid email address';
    });
    return e;
  }

  async function handleAdd(): Promise<void> {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    setServerFieldErrors([]);
    try {
      const reasonTrimmed = reason.trim() || null;
      const newCreditLimit = draft.credit_limit.trim() === '' ? null : Number(draft.credit_limit);
      const newPaymentTerms =
        draft.payment_terms.trim() === '' ? null : Number(draft.payment_terms);
      // A6 — 4-key envelope UNCONDITIONAL. Trigger
      // provenance_distributors_capture INSERT branch (schema.sql L4273-L4290)
      // enriches each key with set_at + set_by only when key is present in
      // NEW.provenance, so unconditional seeding is load-bearing for source
      // attribution across all 4 tracked fields.
      const provenance = {
        credit_limit: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
        payment_terms: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
        is_active: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
        tax_exempt: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
      };

      // M4 — INSERT distributors first, capture id, then loop contacts.
      // M5 — Omit setup_complete (trigger BEFORE INSERT overwrites) and
      // omit set_at/set_by from envelope (trigger enriches).
      const { data: newDist, error: distErr } = await supabase
        .from('distributors')
        .insert({
          name: draft.name.trim(),
          code: draft.code.trim(),
          email: draft.email.trim() || null,
          credit_limit: newCreditLimit,
          payment_terms: newPaymentTerms,
          estimated_tax_pct:
            draft.estimated_tax_pct.trim() === '' ? null : Number(draft.estimated_tax_pct) / 100,
          tax_exempt: draft.tax_exempt,
          is_active: true,
          provenance,
        } as never)
        .select('id')
        .single();
      if (distErr) {
        // M2 — lean on translatePostgrestError for code-UNIQUE violation
        // surfacing (no preflight per A2). Mirrors DistributorCardEdit
        // handleSave L460-L466 field_path extraction.
        const ue = translatePostgrestError(distErr);
        if (ue.field_path !== null) {
          setServerFieldErrors([ue]);
          setSaving(false);
          return;
        }
        throw distErr;
      }
      const newId = (newDist as { id: string }).id;

      // Contacts INSERT loop using returned id as FK source (M4).
      for (const c of draft.contacts) {
        const payload = {
          distributor_id: newId,
          name: c.name.trim(),
          role: c.role.trim() || null,
          email: c.email.trim() || null,
          phone: c.phone.trim() || null,
          is_primary: c.is_primary,
        };
        const { error: cErr } = await supabase
          .from('distributor_contacts')
          .insert(payload as never);
        if (cErr) throw new Error(translatePostgrestError(cErr).message);
      }

      showToast({ kind: 'success', message: `${draft.name.trim()} added` });
      await onAdded();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast({ kind: 'error', message: `Save failed — ${msg}` });
    } finally {
      setSaving(false);
    }
  }

  const inputCls =
    'arbi-input w-full text-xs px-2 py-1 bg-surface-3 border border-border rounded focus:border-accent outline-none';
  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';
  const errCls = 'text-2xs text-danger mt-1';

  return (
    <FormModal
      open={open}
      title="Add distributor"
      size="3xl"
      saving={saving}
      onClose={onCancel}
      footer={
        <>
          <button
            onClick={onCancel}
            disabled={saving}
            className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1 flex items-center gap-1"
          >
            <X size={12} /> Cancel
          </button>
          <button
            onClick={() => void handleAdd()}
            disabled={saving}
            className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 flex items-center gap-1"
          >
            <Save size={12} /> {saving ? 'Saving...' : 'Save Distributor'}
          </button>
        </>
      }
    >
      {/* Identity fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="dist-add-name" className={labelCls}>
            Company Name
          </label>
          <input
            id="dist-add-name"
            value={draft.name}
            onChange={(e) => updateField('name', e.target.value)}
            className={inputCls}
            placeholder="Brooklyn Low Voltage Supply"
          />
          {errors.name && <p className={errCls}>{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="dist-add-code" className={labelCls}>
            Distributor Code
          </label>
          <input
            id="dist-add-code"
            value={draft.code}
            onChange={(e) => updateField('code', e.target.value)}
            className={clsx(inputCls, 'font-mono uppercase')}
            placeholder="BLVS"
          />
          {errors.code && <p className={errCls}>{errors.code}</p>}
          <FieldError fieldPath="code" userErrors={serverFieldErrors} />
        </div>
        <div className="col-span-2">
          <label htmlFor="dist-add-email" className={labelCls}>
            Business Email
          </label>
          <input
            id="dist-add-email"
            type="email"
            value={draft.email}
            onChange={(e) => updateField('email', e.target.value)}
            className={inputCls}
            placeholder="purchasing@example.com"
          />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
      </div>

      {/* A3 — Credit Line visual sub-section. Wraps credit_limit +
          payment_terms inputs under a divider-bordered group with subheader.
          Mirrors the tax-cluster visual pattern at DistributorCardEdit
          L628-L644 (subheader + indented inputs). */}
      <div className="pt-3 border-t border-border-subtle space-y-3">
        <h4 className="text-2xs uppercase tracking-wider text-ink-muted">Credit Line</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="dist-add-credit" className={labelCls}>
              Credit Limit (USD)
            </label>
            <input
              id="dist-add-credit"
              value={draft.credit_limit}
              onChange={(e) => updateField('credit_limit', e.target.value)}
              className={clsx(inputCls, 'font-mono')}
              placeholder="0.00"
              inputMode="decimal"
            />
            {errors.credit_limit && <p className={errCls}>{errors.credit_limit}</p>}
          </div>
          <div>
            <label htmlFor="dist-add-terms" className={labelCls}>
              Payment Terms (days)
            </label>
            <input
              id="dist-add-terms"
              value={draft.payment_terms}
              onChange={(e) => updateField('payment_terms', e.target.value)}
              className={clsx(inputCls, 'font-mono')}
              placeholder="30"
              inputMode="numeric"
            />
            {errors.payment_terms && <p className={errCls}>{errors.payment_terms}</p>}
            {/* Fix Pass A (S-F note) — provenance-tracked, not yet read by the
                profit/credit engine (days-credit-locked unwired → post-launch). */}
            <p className="mt-1 text-2xs text-ink-muted">
              Recorded for reference; not yet used in calculations.
            </p>
          </div>
        </div>
      </div>

      {/* Tax cluster */}
      <div className="pt-3 border-t border-border-subtle space-y-3">
        <div>
          <label htmlFor="dist-add-tax" className={labelCls}>
            Estimated State Tax %
          </label>
          <input
            id="dist-add-tax"
            value={draft.estimated_tax_pct}
            onChange={(e) => updateField('estimated_tax_pct', e.target.value)}
            className={clsx(inputCls, 'font-mono')}
            placeholder="0.00"
            inputMode="decimal"
          />
          {errors.estimated_tax_pct && <p className={errCls}>{errors.estimated_tax_pct}</p>}
          <p className="text-2xs text-ink-muted mt-1">
            Estimate only — replaced by actual on invoice match. Leave blank if resale certificate
            is on file.
          </p>
          <label className="flex items-center gap-2 mt-2 text-xs text-ink-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={draft.tax_exempt}
              onChange={(e) => updateField('tax_exempt', e.target.checked)}
              className="rounded border-border bg-surface-3 text-accent focus:ring-accent/30"
            />
            <span>Tax exempt (resale certificate)</span>
          </label>
          <p className="text-2xs text-ink-muted mt-0.5 ml-6">
            Check when this distributor accepts a resale certificate. Satisfies setup-complete
            without a tax % estimate.
          </p>
          {/* M7 — tax-gap caption. Edit form has the live setup_complete
              badge for this signal; Add form needs the affordance up-front
              since there's no card yet to read state from. */}
          <p className="text-2xs text-ink-muted mt-2 italic">
            This distributor will be flagged setup-incomplete until tax % is entered or Tax exempt
            is checked.
          </p>
        </div>
      </div>

      {/* Contacts editor — lifted verbatim from DistributorCardEdit L662-L775
          minus the existing-contact id branch (Add form contacts have no id
          yet; INSERT loop handles via newId FK per M4). */}
      <div className="pt-3 border-t border-border-subtle">
        <div className="flex items-center justify-between mb-2">
          <span className="text-2xs uppercase tracking-wider text-ink-muted">Contacts</span>
          <button
            onClick={addContact}
            className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
          >
            <Plus size={10} /> Add Contact
          </button>
        </div>
        {draft.contacts.length === 0 ? (
          <p className="text-xs text-ink-muted">No contacts added yet</p>
        ) : (
          <div className="space-y-3">
            {draft.contacts.map((c, i) => (
              <div
                key={c._key}
                className="bg-surface-3 border border-border-subtle rounded p-3 space-y-2"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={`dist-add-c-${c._key}-name`} className={labelCls}>
                      Contact Name
                    </label>
                    <input
                      id={`dist-add-c-${c._key}-name`}
                      value={c.name}
                      onChange={(e) => updateContact(c._key, { name: e.target.value })}
                      className={inputCls}
                      placeholder="Full name"
                    />
                    {errors[`contact_${i}_name`] && (
                      <p className={errCls}>{errors[`contact_${i}_name`]}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`dist-add-c-${c._key}-role`} className={labelCls}>
                      Role / Title
                    </label>
                    <input
                      id={`dist-add-c-${c._key}-role`}
                      value={c.role}
                      onChange={(e) => updateContact(c._key, { role: e.target.value })}
                      className={inputCls}
                      placeholder="Purchasing manager"
                    />
                  </div>
                  <div>
                    <label htmlFor={`dist-add-c-${c._key}-email`} className={labelCls}>
                      Email
                    </label>
                    <input
                      id={`dist-add-c-${c._key}-email`}
                      type="email"
                      value={c.email}
                      onChange={(e) => updateContact(c._key, { email: e.target.value })}
                      className={inputCls}
                      placeholder="name@example.com"
                    />
                    {errors[`contact_${i}_email`] && (
                      <p className={errCls}>{errors[`contact_${i}_email`]}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`dist-add-c-${c._key}-phone`} className={labelCls}>
                      Phone
                    </label>
                    <input
                      id={`dist-add-c-${c._key}-phone`}
                      type="tel"
                      value={c.phone}
                      onChange={(e) => updateContact(c._key, { phone: e.target.value })}
                      className={inputCls}
                      placeholder="555-123-4567"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <button
                    onClick={() => setPrimary(c._key)}
                    aria-pressed={c.is_primary}
                    className={clsx(
                      'text-2xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors',
                      c.is_primary
                        ? 'bg-accent/15 text-accent'
                        : 'text-ink-muted hover:text-ink-secondary hover:bg-surface-4'
                    )}
                    title="Only one contact can be marked primary"
                  >
                    <Star size={10} fill={c.is_primary ? 'currentColor' : 'none'} />
                    {c.is_primary ? 'Primary Contact' : 'Set as Primary'}
                  </button>
                  <button
                    onClick={() => removeContact(c._key)}
                    className="text-2xs text-ink-muted hover:text-danger px-2 py-0.5 rounded flex items-center gap-1 transition-colors"
                    aria-label="Remove contact"
                  >
                    <Trash2 size={10} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* A9 — reason textarea at bottom-of-form. Trims into all 4 provenance
          envelope keys per A6. */}
      <div>
        <label htmlFor="dist-add-reason" className={labelCls}>
          Reason for change (optional)
        </label>
        <textarea
          id="dist-add-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={inputCls}
          rows={2}
          placeholder="Why this change? (optional)"
        />
      </div>
    </FormModal>
  );
}

function DistributorsSection({
  sectionRefs,
  showToast,
  isAdmin,
  staleDays,
}: SectionProps): JSX.Element {
  // Context distributors are loaded once at app start; this section keeps a
  // local mirror so edits surface immediately after save without a full page
  // reload. The mirror reseeds from context whenever it changes (e.g. on
  // initial distributor load).
  const { distributors: ctxDistributors, refreshDistributors: refreshContextDistributors } =
    useApp();
  const [distributors, setDistributors] = useState<Distributor[]>(ctxDistributors);
  const [contacts, setContacts] = useState<Record<string, DistributorContactRow[]>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  // D.14 — toggle for the inline DistributorsAddForm above the list.
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  // Cluster 6: live "Credit Used" (= soft_reserve), batched via get_credit_summaries
  // (one RPC keyed by distributor — replaces the dropped stale distributors.credit_used).
  const [creditUsed, setCreditUsed] = useState<Record<string, number>>({});

  useEffect(() => {
    setDistributors(ctxDistributors);
  }, [ctxDistributors]);

  const loadCreditUsed = useCallback(async (): Promise<void> => {
    const { data } = await callRpcLoose('get_credit_summaries', {});
    const rows =
      (data as Array<{ distributor_id: string; summary: { soft_reserve?: number } }>) ?? [];
    const map: Record<string, number> = {};
    for (const r of rows) map[r.distributor_id] = Number(r.summary?.soft_reserve ?? 0);
    setCreditUsed(map);
  }, []);

  useEffect(() => {
    loadCreditUsed();
  }, [loadCreditUsed]);

  const loadContacts = useCallback(async (): Promise<void> => {
    const { data } = await supabase.from('distributor_contacts').select('*').order('created_at');
    if (!data) return;
    const rows = data as unknown as DistributorContactRow[];
    const map: Record<string, DistributorContactRow[]> = {};
    for (const c of rows) {
      if (!map[c.distributor_id]) map[c.distributor_id] = [];
      map[c.distributor_id].push(c);
    }
    setContacts(map);
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const refreshDistributors = useCallback(async (): Promise<void> => {
    const { data } = await supabase
      .from('distributors')
      .select('*')
      .eq('is_active', true)
      .order('name');
    if (data) setDistributors(data as unknown as Distributor[]);
  }, []);

  const handleSaved = useCallback(async (): Promise<void> => {
    // D.14 M1-a — dual-refresh so post-Edit/Add changes surface immediately
    // both in this section's local mirror AND across the rest of the app
    // (BuyingSession selector, PurchaseOrders scope, etc) without requiring a
    // reload. Context refresh follows SEED-ONCE semantics — operator's
    // activeDistributor selection is preserved.
    await Promise.all([refreshDistributors(), refreshContextDistributors(), loadContacts()]);
    setEditingId(null);
  }, [refreshDistributors, refreshContextDistributors, loadContacts]);

  // D.14 — post-Add handler. Same dual-refresh shape as handleSaved but
  // additionally closes the Add form. Resets editingId defensively (operator
  // can't both edit and add simultaneously).
  const handleAdded = useCallback(async (): Promise<void> => {
    await Promise.all([refreshDistributors(), refreshContextDistributors(), loadContacts()]);
    setShowAddForm(false);
    setEditingId(null);
  }, [refreshDistributors, refreshContextDistributors, loadContacts]);

  // A7 — addAction button (admin-only; same className, Plus icon size, and toggle
  // copy shape as the other Directory sections' add buttons).
  const addAction = isAdmin ? (
    <button
      onClick={() => setShowAddForm(true)}
      className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
    >
      <Plus size={10} /> Add Distributor
    </button>
  ) : null;

  return (
    <SectionCard
      id="distributors"
      title="Distributors"
      sectionRefs={sectionRefs}
      action={addAction}
    >
      {showAddForm && isAdmin && (
        <DistributorsAddForm
          open
          onCancel={() => setShowAddForm(false)}
          onAdded={handleAdded}
          showToast={showToast}
        />
      )}
      {/* A8 — empty-state CTA. Visible only when the list is empty AND the
          Add form isn't open (so the empty-state copy doesn't compete with
          the form). Admin gets the inline CTA; non-admin sees the read-only
          empty copy. */}
      {distributors.length === 0 && !showAddForm ? (
        <div className="py-8 text-center">
          <Building2 size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">No distributors configured</p>
          {isAdmin && (
            <button
              onClick={() => setShowAddForm(true)}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1 mt-3 inline-flex items-center gap-1"
            >
              <Plus size={10} /> Add your first distributor
            </button>
          )}
        </div>
      ) : distributors.length > 0 ? (
        <div className="space-y-4">
          {distributors.map((d) => (
            <div key={d.id}>
              <DistributorCardView
                distributor={d}
                contacts={contacts[d.id] || []}
                isAdmin={isAdmin}
                onEdit={() => setEditingId(d.id)}
                staleDays={staleDays}
                creditUsed={creditUsed[d.id] ?? 0}
              />
              {editingId === d.id && (
                <DistributorCardEdit
                  open
                  distributor={d}
                  contacts={contacts[d.id] || []}
                  onCancel={() => setEditingId(null)}
                  onSaved={handleSaved}
                  showToast={showToast}
                  creditUsed={creditUsed[d.id] ?? 0}
                />
              )}
            </div>
          ))}
        </div>
      ) : null}
    </SectionCard>
  );
}

// ── Manufacturers section ────────────────────────────────────────────────

interface NewMfrDraft {
  name: string;
}

function ManufacturersSection({
  sectionRefs,
  showToast,
  isAdmin,
  staleDays,
}: SectionProps): JSX.Element {
  const { distributors } = useApp();
  const [mfrs, setMfrs] = useState<ManufacturerRow[]>([]);
  const [mds, setMds] = useState<ManufacturerDistributorRow[]>([]);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>('');
  const [editActive, setEditActive] = useState<boolean>(true);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newDraft, setNewDraft] = useState<NewMfrDraft>({ name: '' });
  // Day 84.6 P5 — server-side field errors from translatePostgrestError.
  const [serverFieldErrors, setServerFieldErrors] = useState<UserError[]>([]);
  // Day 83 P6.2 — shared reason state for both Add form + Edit row per L5+L6.
  // Cleared after successful save or cancel of either flow.
  const [reason, setReason] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<ManufacturerRow | null>(null);
  const [brandPickerOpen, setBrandPickerOpen] = useState<string | null>(null);
  const [distAddFor, setDistAddFor] = useState<string | null>(null);
  const [distAddId, setDistAddId] = useState<string>('');
  const [distAddPct, setDistAddPct] = useState<string>('');
  const [distAddMarkup, setDistAddMarkup] = useState<string>('');

  const load = useCallback(async (): Promise<void> => {
    const [mRes, mdRes, bRes] = await Promise.all([
      supabase.from('manufacturers').select('*').order('name'),
      supabase.from('manufacturer_distributors').select('*'),
      supabase.from('brands').select('id, name, manufacturer_id, is_active').order('name'),
    ]);
    setMfrs((mRes.data as unknown as ManufacturerRow[]) || []);
    setMds((mdRes.data as unknown as ManufacturerDistributorRow[]) || []);
    setBrands((bRes.data as unknown as BrandRow[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const distsByMfr = useMemo(() => {
    const map: Record<string, ManufacturerDistributorRow[]> = {};
    for (const md of mds) {
      if (!map[md.manufacturer_id]) map[md.manufacturer_id] = [];
      map[md.manufacturer_id].push(md);
    }
    return map;
  }, [mds]);

  const brandsByMfr = useMemo(() => {
    const map: Record<string, BrandRow[]> = {};
    for (const b of brands) {
      if (!b.manufacturer_id) continue;
      if (!map[b.manufacturer_id]) map[b.manufacturer_id] = [];
      map[b.manufacturer_id].push(b);
    }
    return map;
  }, [brands]);

  const unassignedBrands = useMemo(() => brands.filter((b) => !b.manufacturer_id), [brands]);

  const distributorNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of distributors) map[d.id] = d.name;
    return map;
  }, [distributors]);

  async function handleAdd(): Promise<void> {
    const name = newDraft.name.trim();
    if (!name) {
      showToast({ kind: 'error', message: 'Manufacturer name is required' });
      return;
    }
    setServerFieldErrors([]);
    const reasonTrimmed = reason.trim() || null;
    const { error } = await supabase.from('manufacturers').insert({
      name,
      is_active: true,
      // Day 82 P6.1 — is_active tracked field per L11.
      // Day 83 P6.2 — reason captured from inline textarea (L5+L6).
      provenance: {
        is_active: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
      },
    } as never);
    if (error) {
      const ue = translatePostgrestError(error);
      if (ue.field_path !== null) {
        setServerFieldErrors([ue]);
      } else {
        showToast({ kind: 'error', message: `Save failed — ${ue.message}` });
      }
      return;
    }
    showToast({ kind: 'success', message: `${name} added` });
    setShowAddForm(false);
    setReason('');
    setNewDraft({ name: '' });
    await load();
  }

  function beginEdit(mfr: ManufacturerRow): void {
    setEditingId(mfr.id);
    setEditName(mfr.name);
    setEditActive(mfr.is_active);
  }

  async function saveEdit(mfr: ManufacturerRow): Promise<void> {
    const name = editName.trim();
    if (!name) {
      showToast({ kind: 'error', message: 'Manufacturer name is required' });
      return;
    }
    // Day 83 P6.2 — smart-spread provenance pattern per drift #13 resolution.
    const reasonTrimmed = reason.trim() || null;
    const changedIsActive = editActive !== mfr.is_active;
    const provenance: Record<string, { source: string; reason: string | null }> = {
      ...((mfr.provenance as Record<string, { source: string; reason: string | null }> | null) ??
        {}),
    };
    if (changedIsActive) {
      provenance.is_active = {
        source: PROVENANCE_SOURCE_DIRECTORY_EDIT,
        reason: reasonTrimmed,
      };
    }
    setServerFieldErrors([]);
    const { error } = await supabase
      .from('manufacturers')
      .update({ name, is_active: editActive, provenance } as never)
      .eq('id', mfr.id);
    if (error) {
      const ue = translatePostgrestError(error);
      if (ue.field_path !== null) {
        setServerFieldErrors([ue]);
      } else {
        showToast({ kind: 'error', message: `Save failed — ${ue.message}` });
      }
      return;
    }
    showToast({ kind: 'success', message: `${name} updated` });
    setEditingId(null);
    setReason('');
    await load();
  }

  async function handleDelete(mfr: ManufacturerRow): Promise<void> {
    const { error } = await supabase.from('manufacturers').delete().eq('id', mfr.id);
    if (error) {
      showToast({
        kind: 'error',
        message: `Delete failed — ${translatePostgrestError(error).message}`,
      });
    } else {
      showToast({ kind: 'success', message: `${mfr.name} removed` });
      await load();
    }
    setConfirmDelete(null);
  }

  async function assignBrand(mfrId: string, brandId: string): Promise<void> {
    const { error } = await supabase
      .from('brands')
      .update({ manufacturer_id: mfrId } as never)
      .eq('id', brandId);
    if (error) {
      showToast({
        kind: 'error',
        message: `Assign failed — ${translatePostgrestError(error).message}`,
      });
    } else {
      await load();
    }
    setBrandPickerOpen(null);
  }

  async function unassignBrand(brandId: string, brandName: string): Promise<void> {
    const { error } = await supabase
      .from('brands')
      .update({ manufacturer_id: null } as never)
      .eq('id', brandId);
    if (error) {
      showToast({
        kind: 'error',
        message: `Unassign failed — ${translatePostgrestError(error).message}`,
      });
    } else {
      showToast({ kind: 'success', message: `${brandName} unlinked` });
      await load();
    }
  }

  async function addDistributorLink(mfrId: string): Promise<void> {
    if (!distAddId) {
      showToast({ kind: 'error', message: 'Select a distributor' });
      return;
    }
    const pctNum = Number(distAddPct);
    if (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 100) {
      showToast({ kind: 'error', message: 'Discount must be between 0 and 100' });
      return;
    }
    const markupNum = distAddMarkup.trim() === '' ? 0 : Number(distAddMarkup);
    if (!Number.isFinite(markupNum) || markupNum < 0) {
      showToast({ kind: 'error', message: 'Markup must be a number ≥ 0' });
      return;
    }
    const { error } = await supabase.from('manufacturer_distributors').insert({
      manufacturer_id: mfrId,
      distributor_id: distAddId,
      discount_pct: pctNum,
      markup_pct: markupNum,
    } as never);
    if (error) {
      showToast({
        kind: 'error',
        message: `Save failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: 'Distributor linked' });
    setDistAddFor(null);
    setDistAddId('');
    setDistAddPct('');
    setDistAddMarkup('');
    await load();
  }

  async function updateMarkupPct(mdId: string, pct: number): Promise<void> {
    if (!Number.isFinite(pct) || pct < 0) {
      showToast({ kind: 'error', message: 'Markup must be a number ≥ 0' });
      return;
    }
    const { error } = await supabase
      .from('manufacturer_distributors')
      .update({ markup_pct: pct } as never)
      .eq('id', mdId);
    if (error) {
      showToast({
        kind: 'error',
        message: `Save failed — ${translatePostgrestError(error).message}`,
      });
    } else {
      await load();
    }
  }

  async function updateDiscountPct(mdId: string, pct: number): Promise<void> {
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      showToast({ kind: 'error', message: 'Discount must be between 0 and 100' });
      return;
    }
    const { error } = await supabase
      .from('manufacturer_distributors')
      .update({ discount_pct: pct } as never)
      .eq('id', mdId);
    if (error) {
      showToast({
        kind: 'error',
        message: `Save failed — ${translatePostgrestError(error).message}`,
      });
    } else {
      await load();
    }
  }

  async function removeDistributorLink(mdId: string): Promise<void> {
    const { error } = await supabase.from('manufacturer_distributors').delete().eq('id', mdId);
    if (error) {
      showToast({
        kind: 'error',
        message: `Remove failed — ${translatePostgrestError(error).message}`,
      });
    } else {
      await load();
    }
  }

  const addAction = isAdmin ? (
    <button
      onClick={() => {
        setShowAddForm(true);
        setNewDraft({ name: '' });
        setReason('');
        setServerFieldErrors([]);
      }}
      className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
    >
      <Plus size={10} /> Add Manufacturer
    </button>
  ) : null;

  return (
    <SectionCard
      id="manufacturers"
      title="Manufacturers"
      sectionRefs={sectionRefs}
      action={addAction}
    >
      <FormModal
        open={showAddForm && isAdmin}
        title="Add manufacturer"
        size="md"
        onClose={() => {
          setShowAddForm(false);
          setNewDraft({ name: '' });
          setReason('');
          setServerFieldErrors([]);
        }}
        footer={
          <>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewDraft({ name: '' });
                setReason('');
                setServerFieldErrors([]);
              }}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1"
            >
              Save Manufacturer
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="new-mfr-name"
              className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
            >
              Manufacturer Name
            </label>
            <input
              id="new-mfr-name"
              value={newDraft.name}
              onChange={(e) => setNewDraft((p) => ({ ...p, name: e.target.value }))}
              className="arbi-input w-full text-xs px-2 py-1"
              placeholder="Assa Abloy"
            />
            <FieldError fieldPath="name" userErrors={serverFieldErrors} />
          </div>
        </div>
        {/* Day 83 P6.2 — optional reason capture per L5+L6. */}
        <div>
          <label
            htmlFor="new-mfr-reason"
            className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
          >
            Reason for change (optional)
          </label>
          <textarea
            id="new-mfr-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="arbi-input w-full text-xs px-2 py-1"
            rows={2}
            placeholder="Why this change? (optional)"
          />
        </div>
      </FormModal>

      {loading ? (
        <p className="py-8 text-center text-sm text-ink-muted">Loading...</p>
      ) : mfrs.length === 0 ? (
        <div className="py-8 text-center">
          <Hammer size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">
            No manufacturers yet — add one to start mapping brands and distributor discounts.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {mfrs.map((mfr) => {
            const mfrDists: ManufacturerDistributorRow[] = distsByMfr[mfr.id] || [];
            const mfrBrands: BrandRow[] = brandsByMfr[mfr.id] || [];
            const expanded = expandedId === mfr.id;
            return (
              <div
                key={mfr.id}
                className="bg-surface-2 border border-border-subtle rounded-lg overflow-hidden"
              >
                {/* Summary row */}
                <div className="flex items-center justify-between p-3">
                  <button
                    onClick={() => setExpandedId(expanded ? null : mfr.id)}
                    className="flex items-center gap-2 text-left flex-1 min-w-0"
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <ChevronDown size={14} className="text-ink-muted flex-shrink-0" />
                    ) : (
                      <ChevronRight size={14} className="text-ink-muted flex-shrink-0" />
                    )}
                    <span className="text-sm font-semibold text-ink-bright truncate">
                      {mfr.name}
                    </span>
                    <ProvenanceTooltip
                      provenance={mfr.provenance ?? null}
                      fieldKey="is_active"
                      staleThresholdDays={staleDays}
                      mode="chip"
                    >
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-2xs font-medium',
                          mfr.is_active
                            ? 'bg-success/15 text-success'
                            : 'bg-surface-4 text-ink-muted'
                        )}
                      >
                        {mfr.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </ProvenanceTooltip>
                  </button>
                  <div className="flex items-center gap-3 text-2xs text-ink-muted">
                    <span>
                      <span className="font-mono">{mfrBrands.length}</span> brand
                      {mfrBrands.length !== 1 ? 's' : ''}
                    </span>
                    <span>
                      <span className="font-mono">{mfrDists.length}</span> distributor
                      {mfrDists.length !== 1 ? 's' : ''}
                    </span>
                    {mfrDists.length > 0 && (
                      <span className="font-mono text-ink-secondary">
                        {mfrDists
                          .map((md) => {
                            const code =
                              distributors.find((d) => d.id === md.distributor_id)?.code || '?';
                            return `${code} ${Number(md.discount_pct).toFixed(1)}%`;
                          })
                          .join(' · ')}
                      </span>
                    )}
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => beginEdit(mfr)}
                          className="text-ink-muted hover:text-ink-bright flex items-center gap-1"
                          aria-label={`Edit ${mfr.name}`}
                        >
                          <Pencil size={10} /> Edit
                        </button>
                        <button
                          onClick={() => setConfirmDelete(mfr)}
                          className="text-ink-muted hover:text-danger flex items-center gap-1"
                          aria-label={`Delete ${mfr.name}`}
                        >
                          <Trash2 size={10} /> Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded detail */}
                {expanded && (
                  <div className="border-t border-border-subtle p-3 bg-surface-1/50 space-y-4">
                    {/* Distributor links + discount pcts */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xs uppercase tracking-wider text-ink-muted">
                          Distributors & Discounts
                        </span>
                        {isAdmin && distAddFor !== mfr.id && (
                          <button
                            onClick={() => {
                              setDistAddFor(mfr.id);
                              setDistAddId('');
                              setDistAddPct('');
                              setDistAddMarkup('');
                            }}
                            className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-2 py-0.5 flex items-center gap-1"
                          >
                            <Plus size={10} /> Link Distributor
                          </button>
                        )}
                      </div>
                      {mfrDists.length === 0 && distAddFor !== mfr.id && (
                        <p className="text-xs text-ink-muted">
                          No distributor links yet — add one to record a negotiated discount.
                        </p>
                      )}
                      <div className="space-y-1.5">
                        {mfrDists.map((md) => (
                          <div
                            key={md.id}
                            className="flex items-center gap-3 text-xs bg-surface-3 rounded px-2 py-1"
                          >
                            <span className="text-ink-primary flex-1 truncate">
                              {distributorNameById[md.distributor_id] || '—'}
                            </span>
                            {isAdmin ? (
                              <>
                                <span className="text-2xs text-ink-muted">Disc</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  defaultValue={Number(md.discount_pct).toFixed(2)}
                                  onBlur={(e) => {
                                    const v = Number(e.target.value);
                                    if (Number.isFinite(v) && v !== Number(md.discount_pct)) {
                                      updateDiscountPct(md.id, v);
                                    }
                                  }}
                                  aria-label="Discount percent"
                                  className="arbi-input text-xs font-mono w-16 px-2 py-0.5 text-right"
                                />
                                <span className="text-ink-muted">%</span>
                                <span className="text-2xs text-ink-muted">Markup</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  defaultValue={Number(md.markup_pct ?? 0).toFixed(2)}
                                  onBlur={(e) => {
                                    const v = Number(e.target.value);
                                    if (Number.isFinite(v) && v !== Number(md.markup_pct ?? 0)) {
                                      updateMarkupPct(md.id, v);
                                    }
                                  }}
                                  aria-label="Markup percent"
                                  className="arbi-input text-xs font-mono w-16 px-2 py-0.5 text-right"
                                />
                                <span className="text-ink-muted">%</span>
                                <button
                                  onClick={() => removeDistributorLink(md.id)}
                                  className="text-ink-muted hover:text-danger p-0.5"
                                  aria-label="Remove distributor link"
                                >
                                  <Trash2 size={11} />
                                </button>
                              </>
                            ) : (
                              <span className="font-mono text-ink-secondary">
                                {Number(md.discount_pct).toFixed(2)}% disc ·{' '}
                                {Number(md.markup_pct ?? 0).toFixed(2)}% markup
                              </span>
                            )}
                          </div>
                        ))}
                        {distAddFor === mfr.id && (
                          <div className="flex items-center gap-2 bg-surface-3 rounded px-2 py-1.5">
                            <div className="flex-1 min-w-0">
                              <ArbiSelect
                                value={distAddId}
                                onChange={(v) => setDistAddId(String(v))}
                                options={distributors
                                  .filter((d) => !mfrDists.some((md) => md.distributor_id === d.id))
                                  .map((d) => ({ value: d.id, label: d.name }))}
                                placeholder="Select distributor..."
                              />
                            </div>
                            <span className="text-2xs text-ink-muted">Disc</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={distAddPct}
                              onChange={(e) => setDistAddPct(e.target.value)}
                              aria-label="Discount percent"
                              className="arbi-input text-xs font-mono w-16 px-2 py-0.5 text-right"
                              placeholder="0.00"
                            />
                            <span className="text-ink-muted text-xs">%</span>
                            <span className="text-2xs text-ink-muted">Markup</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={distAddMarkup}
                              onChange={(e) => setDistAddMarkup(e.target.value)}
                              aria-label="Markup percent"
                              className="arbi-input text-xs font-mono w-16 px-2 py-0.5 text-right"
                              placeholder="0.00"
                            />
                            <span className="text-ink-muted text-xs">%</span>
                            <button
                              onClick={() => addDistributorLink(mfr.id)}
                              className="arbi-btn text-2xs bg-accent text-white px-2 py-0.5"
                            >
                              Add
                            </button>
                            <button
                              onClick={() => setDistAddFor(null)}
                              className="text-ink-muted hover:text-ink-bright"
                              aria-label="Cancel"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Brand assignments */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xs uppercase tracking-wider text-ink-muted">
                          Brands ({mfrBrands.length})
                        </span>
                        {isAdmin && unassignedBrands.length > 0 && brandPickerOpen !== mfr.id && (
                          <button
                            onClick={() => setBrandPickerOpen(mfr.id)}
                            className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-2 py-0.5 flex items-center gap-1"
                          >
                            <Plus size={10} /> Assign Brand
                          </button>
                        )}
                      </div>
                      {mfrBrands.length === 0 &&
                        brandPickerOpen !== mfr.id &&
                        (isAdmin && unassignedBrands.length === 0 ? (
                          <p className="text-xs text-ink-muted">
                            No brands assigned yet. Add a brand in the Brands section first, then
                            return here to assign it to {mfr.name}.
                          </p>
                        ) : (
                          <p className="text-xs text-ink-muted">No brands assigned yet.</p>
                        ))}
                      <div className="flex flex-wrap gap-1.5">
                        {mfrBrands.map((b) => (
                          <span
                            key={b.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface-3 text-xs"
                          >
                            <span className="text-ink-primary font-medium">{b.name}</span>
                            {isAdmin && (
                              <button
                                onClick={() => unassignBrand(b.id, b.name || '')}
                                className="text-ink-muted hover:text-danger"
                                aria-label={`Unlink ${b.name}`}
                              >
                                <X size={10} />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {brandPickerOpen === mfr.id && (
                        <div className="mt-2 bg-surface-3 rounded p-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <ArbiSelect
                                value=""
                                onChange={(v) => {
                                  if (v) assignBrand(mfr.id, String(v));
                                }}
                                options={unassignedBrands.map((b) => ({
                                  value: b.id,
                                  label: b.name || '—',
                                }))}
                                placeholder={
                                  unassignedBrands.length === 0
                                    ? 'No unassigned brands available'
                                    : 'Select a brand to assign...'
                                }
                              />
                            </div>
                            <button
                              onClick={() => setBrandPickerOpen(null)}
                              className="text-ink-muted hover:text-ink-bright"
                              aria-label="Close brand picker"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove Manufacturer"
        message={
          confirmDelete
            ? `${confirmDelete.name} will be removed. Any brands currently assigned will be unlinked. This cannot be undone.`
            : ''
        }
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
      <FormModal
        open={editingId !== null && isAdmin}
        title="Edit manufacturer"
        size="md"
        onClose={() => {
          setEditingId(null);
          setReason('');
          setServerFieldErrors([]);
        }}
        footer={
          <>
            <button
              onClick={() => {
                setEditingId(null);
                setReason('');
                setServerFieldErrors([]);
              }}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const mfr = mfrs.find((x) => x.id === editingId);
                if (mfr) void saveEdit(mfr);
              }}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 flex items-center gap-1"
            >
              <Save size={10} /> Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="mfr-edit-name"
              className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
            >
              Name
            </label>
            <input
              id="mfr-edit-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="arbi-input w-full text-xs px-2 py-1"
            />
            <FieldError fieldPath="name" userErrors={serverFieldErrors} />
          </div>
          <div className="col-span-2">
            <button
              type="button"
              onClick={() => setEditActive((v) => !v)}
              aria-pressed={editActive}
              className={clsx(
                'text-2xs px-2 py-0.5 rounded flex items-center gap-1',
                editActive ? 'bg-success/15 text-success' : 'bg-surface-4 text-ink-muted'
              )}
            >
              <Check size={10} /> {editActive ? 'Active' : 'Inactive'}
            </button>
          </div>
        </div>
        {/* Day 83 P6.2 — optional reason capture per L5+L6. */}
        <div>
          <label
            htmlFor="mfr-edit-reason"
            className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
          >
            Reason for change (optional)
          </label>
          <textarea
            id="mfr-edit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="arbi-input w-full text-xs px-2 py-1"
            rows={2}
            placeholder="Why this change? (optional)"
          />
        </div>
      </FormModal>
    </SectionCard>
  );
}

// ── Brands section ────────────────────────────────────────────────────────

interface BrandDraft {
  name: string;
  manufacturer_id: string;
  restocking_fee_pct: string;
  gating_status: string;
  marketplace_permissions: Record<string, boolean>;
  is_active: boolean;
  enforces_map: boolean;
}

const EMPTY_BRAND_DRAFT: BrandDraft = {
  name: '',
  manufacturer_id: '',
  restocking_fee_pct: '',
  gating_status: 'open',
  marketplace_permissions: {},
  is_active: true,
  enforces_map: false,
};

function brandToDraft(b: BrandRow): BrandDraft {
  return {
    name: b.name ?? '',
    manufacturer_id: b.manufacturer_id ?? '',
    restocking_fee_pct: b.restocking_fee_pct != null ? String(b.restocking_fee_pct) : '',
    gating_status: b.gating_status ?? 'open',
    marketplace_permissions: b.marketplace_permissions ?? {},
    is_active: b.is_active ?? true,
    enforces_map: b.enforces_map ?? false,
  };
}

function buildBrandPayload(d: BrandDraft): Record<string, unknown> | { error: string } {
  const name = d.name.trim();
  if (!name) return { error: 'Brand name is required' };
  const pctStr = d.restocking_fee_pct.trim();
  let pct: number | null = null;
  if (pctStr) {
    const n = Number(pctStr);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      return { error: 'Restocking fee must be between 0 and 100' };
    }
    pct = n;
  }
  return {
    name,
    manufacturer_id: d.manufacturer_id || null,
    restocking_fee_pct: pct,
    gating_status: d.gating_status,
    marketplace_permissions: d.marketplace_permissions,
    is_active: d.is_active,
    enforces_map: d.enforces_map,
  };
}

const GATING_BADGE: Record<string, string> = {
  open: 'bg-success/15 text-success',
  gated: 'bg-warning/15 text-warning',
  restricted: 'bg-danger/15 text-danger',
};

const GATING_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'gated', label: 'Gated' },
  { value: 'restricted', label: 'Restricted' },
];

interface BrandFormProps {
  draft: BrandDraft;
  onChange: (d: BrandDraft) => void;
  mfrOptions: Array<{ value: string; label: string }>;
  marketplaces: Array<{ id: string; channel: string; label: string }>;
  // Day 84.6 P5 — translator output for inline server-error rendering below name input.
  serverFieldErrors?: UserError[];
}

function BrandForm({
  draft,
  onChange,
  mfrOptions,
  marketplaces,
  serverFieldErrors,
}: BrandFormProps): JSX.Element {
  const set = <K extends keyof BrandDraft>(k: K, v: BrandDraft[K]): void =>
    onChange({ ...draft, [k]: v });
  const togglePerm = (channel: string): void => {
    const next = { ...draft.marketplace_permissions };
    next[channel] = !next[channel];
    set('marketplace_permissions', next);
  };
  const id = useId();
  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label htmlFor={`${id}-name`} className={labelCls}>
          Name
        </label>
        <input
          id={`${id}-name`}
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1 font-mono"
        />
        <FieldError fieldPath="name" userErrors={serverFieldErrors ?? []} />
      </div>
      <div>
        <label className={labelCls}>
          Manufacturer
          <ArbiSelect
            value={draft.manufacturer_id}
            onChange={(v) => set('manufacturer_id', v)}
            options={[{ value: '', label: 'Unassigned' }, ...mfrOptions]}
            placeholder="Unassigned"
          />
        </label>
      </div>
      <div>
        <label htmlFor={`${id}-restocking`} className={labelCls}>
          Restocking fee %
        </label>
        <input
          id={`${id}-restocking`}
          type="number"
          step="0.01"
          value={draft.restocking_fee_pct}
          onChange={(e) => set('restocking_fee_pct', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1 font-mono"
          placeholder="0–100"
        />
        {/* K4 (ghost-config) — restocking_fee_pct is recorded but not read by the
            profit/returns engine; the "% restock" card chip stays as an honest
            record. Labeled so it isn't mistaken for a live input. */}
        <p className="mt-1 text-2xs text-ink-muted">
          Recorded for reference; not used in calculations.
        </p>
      </div>
      <div>
        <label className={labelCls}>
          Gating status
          <ArbiSelect
            value={draft.gating_status}
            onChange={(v) => set('gating_status', v)}
            options={GATING_OPTIONS}
          />
        </label>
      </div>
      <div className="col-span-2" role="group" aria-label="Marketplace permissions">
        <span className={labelCls}>Marketplace permissions</span>
        {marketplaces.length === 0 ? (
          <p className="text-2xs text-ink-muted">No marketplaces configured.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {marketplaces.map((m) => {
              const checked = !!draft.marketplace_permissions[m.channel];
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => togglePerm(m.channel)}
                  aria-pressed={checked}
                  className={clsx(
                    'text-2xs px-2 py-0.5 rounded border flex items-center gap-1',
                    checked
                      ? 'bg-accent/15 text-accent border-accent/40'
                      : 'bg-surface-3 text-ink-muted border-border-subtle'
                  )}
                >
                  {checked ? <Check size={10} /> : null}
                  {m.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="col-span-2">
        <button
          type="button"
          onClick={() => set('is_active', !draft.is_active)}
          aria-pressed={draft.is_active}
          className={clsx(
            'text-2xs px-2 py-0.5 rounded flex items-center gap-1',
            draft.is_active ? 'bg-success/15 text-success' : 'bg-surface-4 text-ink-muted'
          )}
        >
          <Check size={10} /> {draft.is_active ? 'Active' : 'Inactive'}
        </button>
      </div>
      <div className="col-span-2">
        <button
          type="button"
          onClick={() => set('enforces_map', !draft.enforces_map)}
          aria-pressed={draft.enforces_map}
          aria-describedby={`${id}-map-hint`}
          className={clsx(
            'text-2xs px-2 py-0.5 rounded flex items-center gap-1',
            draft.enforces_map ? 'bg-warning/15 text-warning' : 'bg-surface-4 text-ink-muted'
          )}
        >
          <Check size={10} /> {draft.enforces_map ? 'Enforces MAP' : 'No MAP enforcement'}
        </button>
        <p id={`${id}-map-hint`} className="text-2xs text-ink-muted mt-1">
          When enabled, flags products selling below their Minimum Advertised Price. Recalculate
          Profitability after toggling to refresh MAP flags.
        </p>
      </div>
    </div>
  );
}

function BrandsSection({ sectionRefs, showToast, isAdmin, staleDays }: SectionProps): JSX.Element {
  // Day 64 A.6b: widen Add gate to admin+user (matches the brands_insert_admin_user
  // RLS policy renamed/widened by Phase 1.4). Edit + Delete stay admin-only —
  // brands_update_admin + brands_delete_admin remain strict; widening only the
  // INSERT side enables VAs to create missing brands during buying-session
  // workflows without admin escalation.
  const { role } = useApp();
  const canAdd = role === 'admin' || role === 'user';
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [mfrLookup, setMfrLookup] = useState<Record<string, string>>({});
  const [mfrOptions, setMfrOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [marketplaces, setMarketplaces] = useState<
    Array<{ id: string; channel: string; label: string }>
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newDraft, setNewDraft] = useState<BrandDraft>(EMPTY_BRAND_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<BrandDraft>(EMPTY_BRAND_DRAFT);
  const [confirmDelete, setConfirmDelete] = useState<BrandRow | null>(null);
  const [mergeFor, setMergeFor] = useState<BrandRow | null>(null);
  const [mergeWinnerId, setMergeWinnerId] = useState<string>('');
  const [merging, setMerging] = useState<boolean>(false);
  const [mergeFkCount, setMergeFkCount] = useState<number | null>(null);
  const [mergeTextCount, setMergeTextCount] = useState<number | null>(null);
  // Day 83 P6.2 — shared reason state per L5+L6 (Add form + Edit form;
  // markReviewed has no form, accepts no-reason case per R2).
  const [reason, setReason] = useState<string>('');
  // Day 84.6 P5 — server-side field errors from translatePostgrestError.
  const [serverFieldErrors, setServerFieldErrors] = useState<UserError[]>([]);

  // Day 78 P5.3 B.7/D.6 — review-queue filter state.
  type BrandFilter = 'all' | 'to_review' | 'reviewed';
  const [filter, setFilter] = useState<BrandFilter>('all');

  const load = useCallback(async (): Promise<void> => {
    const [bRes, mRes, mpRes] = await Promise.all([
      supabase.from('brands').select('*').order('name'),
      supabase.from('manufacturers').select('id, name').order('name'),
      supabase.from('marketplaces').select('id, channel, display_name').order('display_name'),
    ]);
    setBrands((bRes.data as unknown as BrandRow[]) || []);
    const lookup: Record<string, string> = {};
    const opts: Array<{ value: string; label: string }> = [];
    for (const m of (mRes.data as unknown as Array<{ id: string; name: string }>) || []) {
      lookup[m.id] = m.name;
      opts.push({ value: m.id, label: m.name });
    }
    setMfrLookup(lookup);
    setMfrOptions(opts);
    const mps = (
      (mpRes.data as unknown as Array<{
        id: string;
        channel: string;
        display_name: string;
      }>) || []
    ).map((m) => ({ id: m.id, channel: m.channel, label: m.display_name }));
    setMarketplaces(mps);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAdd(): Promise<void> {
    const payload = buildBrandPayload(newDraft);
    if ('error' in payload) {
      showToast({ kind: 'error', message: String(payload.error) });
      return;
    }
    // Day 82 P6.1 — enforces_map, is_active, gating_status tracked fields per
    // L11 (auto_seeded also tracked, set in markReviewed path, not here).
    // Day 83 P6.2 — reason captured from shared textarea (L5+L6).
    const reasonTrimmed = reason.trim() || null;
    const payloadWithProvenance = {
      ...payload,
      provenance: {
        enforces_map: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
        is_active: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
        gating_status: { source: PROVENANCE_SOURCE_DIRECTORY_CREATE, reason: reasonTrimmed },
      },
    };
    setServerFieldErrors([]);
    const { error } = await supabase.from('brands').insert(payloadWithProvenance as never);
    if (error) {
      const ue = translatePostgrestError(error);
      if (ue.field_path !== null) {
        setServerFieldErrors([ue]);
      } else {
        showToast({ kind: 'error', message: `Save failed — ${ue.message}` });
      }
      return;
    }
    showToast({ kind: 'success', message: `${payload.name as string} added` });
    setShowAddForm(false);
    setNewDraft(EMPTY_BRAND_DRAFT);
    setReason('');
    await load();
  }

  function beginEdit(b: BrandRow): void {
    setEditingId(b.id);
    setEditDraft(brandToDraft(b));
  }

  async function saveEdit(b: BrandRow): Promise<void> {
    const payload = buildBrandPayload(editDraft);
    if ('error' in payload) {
      showToast({ kind: 'error', message: String(payload.error) });
      return;
    }
    // Day 83 P6.2 — smart-spread provenance per drift #13 resolution.
    const reasonTrimmed = reason.trim() || null;
    const newEnforcesMap = payload.enforces_map as boolean;
    const newIsActive = payload.is_active as boolean;
    const newGatingStatus = payload.gating_status as string;
    const changedEnforcesMap = newEnforcesMap !== (b.enforces_map ?? null);
    const changedIsActive = newIsActive !== (b.is_active ?? null);
    const changedGatingStatus = newGatingStatus !== (b.gating_status ?? null);
    const provenance: Record<string, { source: string; reason: string | null }> = {
      ...((b.provenance as Record<string, { source: string; reason: string | null }> | null) ?? {}),
    };
    if (changedEnforcesMap) {
      provenance.enforces_map = {
        source: PROVENANCE_SOURCE_DIRECTORY_EDIT,
        reason: reasonTrimmed,
      };
    }
    if (changedIsActive) {
      provenance.is_active = {
        source: PROVENANCE_SOURCE_DIRECTORY_EDIT,
        reason: reasonTrimmed,
      };
    }
    if (changedGatingStatus) {
      provenance.gating_status = {
        source: PROVENANCE_SOURCE_DIRECTORY_EDIT,
        reason: reasonTrimmed,
      };
    }
    setServerFieldErrors([]);
    const { error } = await supabase
      .from('brands')
      .update({ ...payload, provenance } as never)
      .eq('id', b.id);
    if (error) {
      const ue = translatePostgrestError(error);
      if (ue.field_path !== null) {
        setServerFieldErrors([ue]);
      } else {
        showToast({ kind: 'error', message: `Save failed — ${ue.message}` });
      }
      return;
    }
    showToast({ kind: 'success', message: `${payload.name as string} updated` });
    setEditingId(null);
    setReason('');
    await load();
  }

  async function handleDelete(b: BrandRow): Promise<void> {
    const { error } = await supabase.from('brands').delete().eq('id', b.id);
    if (error) {
      showToast({
        kind: 'error',
        message: `Delete failed — ${translatePostgrestError(error).message}`,
      });
    } else {
      showToast({ kind: 'success', message: `${b.name ?? 'Brand'} removed` });
      await load();
    }
    setConfirmDelete(null);
  }

  // Day 78 P5.3 B.7/D.6 — one-click review action (Q4 (a) lock).
  // Day 83 P6.2 — R2 lock: source=directory_edit (no 6th source);
  // reason=null (no form/textarea on this one-click affordance);
  // smart-spread per drift #13 preserves prior algopix_discovery envelopes
  // (auto_seeded + is_active from Files.tsx INSERT).
  async function markReviewed(b: BrandRow): Promise<void> {
    const provenance: Record<string, { source: string; reason: string | null }> = {
      ...((b.provenance as Record<string, { source: string; reason: string | null }> | null) ?? {}),
      auto_seeded: { source: PROVENANCE_SOURCE_DIRECTORY_EDIT, reason: null },
    };
    const { error } = await supabase
      .from('brands')
      .update({ auto_seeded: false, provenance } as never)
      .eq('id', b.id);
    if (error) {
      showToast({
        kind: 'error',
        message: `Mark reviewed failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: `${b.name ?? 'Brand'} marked reviewed` });
    await load();
  }

  // Day 78 P5.3 B.7/D.6 — derived list for filter view (Q3 (1) lock).
  const visibleBrands = useMemo(() => {
    if (filter === 'to_review') return brands.filter((b) => b.auto_seeded === true);
    if (filter === 'reviewed') return brands.filter((b) => b.auto_seeded === false);
    return brands;
  }, [brands, filter]);

  const toReviewCount = useMemo(
    () => brands.filter((b) => b.auto_seeded === true).length,
    [brands]
  );

  // Pre-merge counts depend only on the loser; fetch as soon as the modal opens.
  // Display is gated until a winner is picked (panel below).
  useEffect(() => {
    if (!mergeFor) {
      setMergeFkCount(null);
      setMergeTextCount(null);
      return;
    }
    let cancelled = false;
    (async (): Promise<void> => {
      const fkRes = await supabase
        .from('products')
        .select('id', { count: 'exact', head: true })
        .eq('brand_id', mergeFor.id);
      const trimmed = (mergeFor.name ?? '').trim();
      const textRes = trimmed
        ? await supabase
            .from('products')
            .select('id', { count: 'exact', head: true })
            .ilike('brand', trimmed)
        : { count: 0 };
      if (cancelled) return;
      setMergeFkCount(fkRes.count ?? 0);
      setMergeTextCount(textRes.count ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [mergeFor]);

  async function handleMerge(): Promise<void> {
    if (!mergeFor || !mergeWinnerId) return;
    setMerging(true);
    // Day 84.6 P4.6 — merge_brands envelope-extract per Sol-lock 3 FULL-REWRITE +
    // Path B audit 🔴 silent-broken fix. Pre-Phase-1 returned flat jsonb shape
    // (products_fk_reassigned, products_text_updated, ..., winner_name at top
    // level). Post-migration returns { ok, data: { ...flat_shape }, userErrors }
    // envelope. The `data as { ... }` flat-cast was reading envelope fields as
    // undefined; toast showed "undefined FKs + undefined text refs".
    type MergeBrandsResult = {
      products_fk_reassigned: number;
      products_text_updated: number;
      params_reassigned: number;
      loser_deleted: boolean;
      loser_name: string;
      winner_name: string;
    };
    const mergeResult = await supabase.rpc('merge_brands', {
      p_loser_id: mergeFor.id,
      p_winner_id: mergeWinnerId,
    });
    setMerging(false);
    const env = extractEnvelope<MergeBrandsResult>(mergeResult);
    if (!env.isOk) {
      showToast({
        kind: 'error',
        message: `Merge failed — ${env.userErrors[0]?.message ?? 'unknown'}`,
      });
      return;
    }
    const result = env.data!;
    showToast({
      kind: 'success',
      message: `Merged into ${result.winner_name}. Reassigned ${result.products_fk_reassigned} FKs + ${result.products_text_updated} text refs.`,
    });
    setMergeFor(null);
    setMergeWinnerId('');
    await load();
  }

  const addAction = canAdd ? (
    <button
      onClick={() => {
        setShowAddForm(true);
        setNewDraft(EMPTY_BRAND_DRAFT);
        setReason('');
        setServerFieldErrors([]);
      }}
      className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
    >
      <Plus size={10} /> Add Brand
    </button>
  ) : null;

  return (
    <SectionCard
      id="brands"
      title={toReviewCount > 0 ? `Brands (${toReviewCount} to review)` : 'Brands'}
      sectionRefs={sectionRefs}
      action={addAction}
    >
      <FormModal
        open={showAddForm && canAdd}
        title="Add brand"
        size="xl"
        onClose={() => {
          setShowAddForm(false);
          setNewDraft(EMPTY_BRAND_DRAFT);
          setReason('');
          setServerFieldErrors([]);
        }}
        footer={
          <>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewDraft(EMPTY_BRAND_DRAFT);
                setReason('');
                setServerFieldErrors([]);
              }}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1"
            >
              Save Brand
            </button>
          </>
        }
      >
        <BrandForm
          draft={newDraft}
          onChange={setNewDraft}
          mfrOptions={mfrOptions}
          marketplaces={marketplaces}
          serverFieldErrors={serverFieldErrors}
        />
        {/* Day 83 P6.2 — optional reason capture per L5+L6. */}
        <div>
          <label
            htmlFor="new-brand-reason"
            className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
          >
            Reason for change (optional)
          </label>
          <textarea
            id="new-brand-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="arbi-input w-full text-xs px-2 py-1"
            rows={2}
            placeholder="Why this change? (optional)"
          />
        </div>
      </FormModal>

      {loading ? (
        <p className="py-8 text-center text-sm text-ink-muted">Loading...</p>
      ) : brands.length === 0 ? (
        <div className="py-8 text-center">
          <Tag size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">
            No brands yet — brands are added here and matched to products during import
          </p>
        </div>
      ) : (
        <>
          {/* Day 78 P5.3 B.7/D.6 — segmented filter strip (Q3 (1) lock) */}
          <div className="flex items-center gap-1 mb-3 text-2xs">
            <button
              onClick={() => setFilter('all')}
              className={clsx(
                'px-2 py-1 rounded',
                filter === 'all' ? 'bg-accent text-white' : 'bg-surface-3 text-ink-secondary'
              )}
            >
              All ({brands.length})
            </button>
            <button
              onClick={() => setFilter('to_review')}
              className={clsx(
                'px-2 py-1 rounded',
                filter === 'to_review' ? 'bg-warning text-white' : 'bg-surface-3 text-ink-secondary'
              )}
            >
              To Review ({toReviewCount})
            </button>
            <button
              onClick={() => setFilter('reviewed')}
              className={clsx(
                'px-2 py-1 rounded',
                filter === 'reviewed' ? 'bg-accent text-white' : 'bg-surface-3 text-ink-secondary'
              )}
            >
              Reviewed ({brands.length - toReviewCount})
            </button>
          </div>
          {visibleBrands.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-muted">
              {filter === 'to_review'
                ? 'No brands to review — all auto-seeded brands have been completed'
                : 'No brands in this filter'}
            </p>
          ) : (
            <div className="divide-y divide-border-subtle">
              {visibleBrands.map((b) => {
                return (
                  <div key={b.id} className="py-3 px-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-mono text-ink-primary font-medium">
                          {b.name}
                        </span>
                        <ProvenanceTooltip
                          provenance={b.provenance ?? null}
                          fieldKey="gating_status"
                          staleThresholdDays={staleDays}
                          mode="chip"
                        >
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded text-2xs font-medium',
                              (b.gating_status && GATING_BADGE[b.gating_status]) ||
                                GATING_BADGE.open
                            )}
                          >
                            {b.gating_status || 'Open'}
                          </span>
                        </ProvenanceTooltip>
                        {b.manufacturer_id ? (
                          <span
                            className="px-1.5 py-0.5 rounded text-2xs bg-accent/10 text-accent"
                            title="Assigned manufacturer"
                          >
                            {mfrLookup[b.manufacturer_id] || 'Manufacturer'}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-2xs bg-surface-3 text-ink-muted">
                            Unassigned
                          </span>
                        )}
                        {b.auto_seeded && (
                          <ProvenanceTooltip
                            provenance={b.provenance ?? null}
                            fieldKey="auto_seeded"
                            staleThresholdDays={staleDays}
                            mode="chip"
                          >
                            <span className="px-1.5 py-0.5 rounded text-2xs bg-warning/15 text-warning">
                              To Review
                            </span>
                          </ProvenanceTooltip>
                        )}
                        {b.enforces_map != null && (
                          <ProvenanceTooltip
                            provenance={b.provenance ?? null}
                            fieldKey="enforces_map"
                            staleThresholdDays={staleDays}
                            mode="chip"
                          >
                            <span
                              className={clsx(
                                'px-1.5 py-0.5 rounded text-2xs font-medium',
                                b.enforces_map
                                  ? 'bg-info/15 text-info'
                                  : 'bg-surface-3 text-ink-muted'
                              )}
                            >
                              {b.enforces_map ? 'MAP enforced' : 'MAP off'}
                            </span>
                          </ProvenanceTooltip>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-ink-secondary">
                        {b.restocking_fee_pct != null && (
                          <span className="font-mono">{b.restocking_fee_pct}% restock</span>
                        )}
                        <ProvenanceTooltip
                          provenance={b.provenance ?? null}
                          fieldKey="is_active"
                          staleThresholdDays={staleDays}
                          mode="chip"
                        >
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded text-2xs font-medium',
                              b.is_active
                                ? 'bg-success/15 text-success'
                                : 'bg-surface-3 text-ink-muted'
                            )}
                          >
                            {b.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </ProvenanceTooltip>
                        {isAdmin && (
                          <>
                            {b.auto_seeded && (
                              <button
                                onClick={() => markReviewed(b)}
                                className="text-ink-muted hover:text-success flex items-center gap-1"
                                aria-label={`Mark ${b.name ?? 'brand'} as reviewed`}
                              >
                                <Check size={10} /> Mark Reviewed
                              </button>
                            )}
                            <button
                              onClick={() => beginEdit(b)}
                              className="text-ink-muted hover:text-ink-bright flex items-center gap-1"
                              aria-label={`Edit ${b.name ?? 'brand'}`}
                            >
                              <Pencil size={10} /> Edit
                            </button>
                            <button
                              onClick={() => {
                                setMergeFor(b);
                                setMergeWinnerId('');
                              }}
                              className="text-ink-muted hover:text-warning flex items-center gap-1"
                              aria-label={`Merge ${b.name ?? 'brand'} into another brand`}
                            >
                              <GitMerge size={10} /> Merge
                            </button>
                            <button
                              onClick={() => setConfirmDelete(b)}
                              className="text-ink-muted hover:text-danger flex items-center gap-1"
                              aria-label={`Delete ${b.name ?? 'brand'}`}
                            >
                              <Trash2 size={10} /> Remove
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <FormModal
        open={editingId !== null && isAdmin}
        title="Edit brand"
        size="xl"
        onClose={() => {
          setEditingId(null);
          setReason('');
          setServerFieldErrors([]);
        }}
        footer={
          <>
            <button
              onClick={() => {
                setEditingId(null);
                setReason('');
                setServerFieldErrors([]);
              }}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const b = brands.find((x) => x.id === editingId);
                if (b) void saveEdit(b);
              }}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 flex items-center gap-1"
            >
              <Save size={10} /> Save
            </button>
          </>
        }
      >
        <BrandForm
          draft={editDraft}
          onChange={setEditDraft}
          mfrOptions={mfrOptions}
          marketplaces={marketplaces}
          serverFieldErrors={serverFieldErrors}
        />
        {/* Day 83 P6.2 — optional reason capture per L5+L6. */}
        <div>
          <label
            htmlFor="brand-edit-reason"
            className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
          >
            Reason for change (optional)
          </label>
          <textarea
            id="brand-edit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="arbi-input w-full text-xs px-2 py-1"
            rows={2}
            placeholder="Why this change? (optional)"
          />
        </div>
      </FormModal>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove Brand"
        message={
          confirmDelete
            ? `${confirmDelete.name ?? 'Brand'} will be removed. Products currently linked will be unlinked. This cannot be undone.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />

      <BrandMergeModal
        loser={mergeFor}
        winnerId={mergeWinnerId}
        onWinnerChange={setMergeWinnerId}
        merging={merging}
        brands={brands}
        mfrLookup={mfrLookup}
        marketplaces={marketplaces}
        fkCount={mergeFkCount}
        textCount={mergeTextCount}
        onCancel={() => {
          setMergeFor(null);
          setMergeWinnerId('');
        }}
        onConfirm={handleMerge}
      />
    </SectionCard>
  );
}

// ── BrandMergeModal ───────────────────────────────────────────────────────

interface BrandMergeModalProps {
  loser: BrandRow | null;
  winnerId: string;
  onWinnerChange: (id: string) => void;
  merging: boolean;
  brands: BrandRow[];
  mfrLookup: Record<string, string>;
  marketplaces: Array<{ id: string; channel: string; label: string }>;
  fkCount: number | null;
  textCount: number | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function summarizeMarketplacePerms(
  perms: Record<string, boolean> | null | undefined,
  marketplaces: Array<{ id: string; channel: string; label: string }>
): string {
  if (!perms) return 'none';
  const mpById: Record<string, string> = {};
  for (const m of marketplaces) mpById[m.id] = m.label;
  const enabled = Object.entries(perms)
    .filter(([, v]) => v)
    .map(([k]) => mpById[k] ?? k);
  return enabled.length ? enabled.join(', ') : 'none';
}

function permsEqual(
  a: Record<string, boolean> | null | undefined,
  b: Record<string, boolean> | null | undefined
): boolean {
  const ae = Object.entries(a ?? {})
    .filter(([, v]) => v)
    .map(([k]) => k)
    .sort();
  const be = Object.entries(b ?? {})
    .filter(([, v]) => v)
    .map(([k]) => k)
    .sort();
  if (ae.length !== be.length) return false;
  for (let i = 0; i < ae.length; i++) if (ae[i] !== be[i]) return false;
  return true;
}

function BrandMergeModal({
  loser,
  winnerId,
  onWinnerChange,
  merging,
  brands,
  mfrLookup,
  marketplaces,
  fkCount,
  textCount,
  onCancel,
  onConfirm,
}: BrandMergeModalProps): JSX.Element | null {
  useEffect(() => {
    if (!loser) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !merging) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [loser, merging, onCancel]);

  if (!loser) return null;

  const winner = brands.find((b) => b.id === winnerId) ?? null;

  const winnerOptions = brands
    .filter((b) => b.id !== loser.id)
    .map((b) => ({ value: b.id, label: b.name ?? '(unnamed)' }));

  const warnings: string[] = [];
  if (winner) {
    if (loser.manufacturer_id && !winner.manufacturer_id) {
      warnings.push(
        `Loser is assigned to manufacturer ${mfrLookup[loser.manufacturer_id] ?? '(unknown)'} — winner is unassigned. Discarded on merge.`
      );
    } else if (
      loser.manufacturer_id &&
      winner.manufacturer_id &&
      loser.manufacturer_id !== winner.manufacturer_id
    ) {
      warnings.push(
        `Loser manufacturer (${mfrLookup[loser.manufacturer_id] ?? '(unknown)'}) differs from winner (${mfrLookup[winner.manufacturer_id] ?? '(unknown)'}). Loser's value discarded.`
      );
    }
    if ((loser.gating_status ?? 'open') !== (winner.gating_status ?? 'open')) {
      warnings.push(
        `Gating: loser=${loser.gating_status ?? 'open'} vs winner=${winner.gating_status ?? 'open'}. Winner's value kept.`
      );
    }
    if (
      loser.restocking_fee_pct != null &&
      loser.restocking_fee_pct !== winner.restocking_fee_pct
    ) {
      warnings.push(
        `Restocking fee: loser=${loser.restocking_fee_pct}% vs winner=${winner.restocking_fee_pct ?? 'unset'}. Loser's value discarded.`
      );
    }
    if (!permsEqual(loser.marketplace_permissions, winner.marketplace_permissions)) {
      warnings.push(
        `Marketplace permissions: loser=[${summarizeMarketplacePerms(loser.marketplace_permissions, marketplaces)}] discarded.`
      );
    }
    if (winner.auto_seeded) {
      warnings.push('Winner is auto-seeded — verify this is the canonical brand.');
    }
  }

  const totalToReassign =
    fkCount != null && textCount != null ? Math.max(fkCount, textCount) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        onClick={merging ? undefined : onCancel}
        aria-label="Close merge dialog"
        tabIndex={-1}
      />
      <div
        className="relative bg-surface-2 border border-border rounded-xl w-[520px] max-h-[85vh] flex flex-col shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={`Merge brand ${loser.name ?? ''}`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <h3 className="text-sm font-semibold text-ink-bright">
            Merge Brand: {loser.name ?? '(unnamed)'}
          </h3>
          <button
            onClick={onCancel}
            disabled={merging}
            className="p-1 text-ink-muted hover:text-ink-primary transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <label className="text-2xs uppercase tracking-wider text-ink-muted block mb-1">
              Merge into
              <ArbiSelect
                value={winnerId}
                onChange={onWinnerChange}
                options={winnerOptions}
                placeholder="Select brand to merge into..."
              />
            </label>
          </div>

          {winner && (
            <>
              <div className="rounded-lg bg-surface-3 border border-border-subtle p-3 space-y-1.5">
                <p className="text-2xs uppercase tracking-wider text-ink-muted">Pre-merge review</p>
                {warnings.length === 0 ? (
                  <p className="text-xs text-ink-secondary">
                    No metadata conflicts. Merge will reassign products only.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {warnings.map((w) => (
                      <li
                        key={w}
                        className="text-xs text-warning flex items-start gap-1.5 leading-relaxed"
                      >
                        <span className="text-warning mt-0.5">•</span>
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {totalToReassign != null && (
                <p className="text-xs text-ink-muted">
                  Approximately {totalToReassign} product{totalToReassign === 1 ? '' : 's'} will be
                  reassigned ({fkCount ?? 0} via brand_id, {textCount ?? 0} via text match).
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border-subtle">
          <button
            onClick={onCancel}
            disabled={merging}
            className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-3 py-1.5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!winnerId || merging}
            className="arbi-btn text-xs bg-warning text-white hover:bg-warning/90 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1"
          >
            <GitMerge size={10} />
            {merging ? 'Merging...' : winner ? `Merge into ${winner.name ?? '(unnamed)'}` : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Categories section ────────────────────────────────────────────────────

interface CategoryDraft {
  name: string;
  parent_id: string;
  is_active: boolean;
}

const EMPTY_CATEGORY_DRAFT: CategoryDraft = { name: '', parent_id: '', is_active: true };

function categoryToDraft(c: CategoryRow): CategoryDraft {
  return {
    name: c.name ?? '',
    parent_id: c.parent_id ?? '',
    is_active: c.is_active ?? true,
  };
}

function buildCategoryPayload(d: CategoryDraft): Record<string, unknown> | { error: string } {
  const name = d.name.trim();
  if (!name) return { error: 'Category name is required' };
  return {
    name,
    parent_id: d.parent_id || null,
    is_active: d.is_active,
  };
}

interface CategoryFormProps {
  draft: CategoryDraft;
  onChange: (d: CategoryDraft) => void;
  parentOptions: Array<{ value: string; label: string }>;
}

function CategoryForm({ draft, onChange, parentOptions }: CategoryFormProps): JSX.Element {
  const set = <K extends keyof CategoryDraft>(k: K, v: CategoryDraft[K]): void =>
    onChange({ ...draft, [k]: v });
  const id = useId();
  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label htmlFor={`${id}-name`} className={labelCls}>
          Name
        </label>
        <input
          id={`${id}-name`}
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1"
        />
      </div>
      <div>
        <label className={labelCls}>
          Parent category
          <ArbiSelect
            value={draft.parent_id}
            onChange={(v) => set('parent_id', v)}
            options={[{ value: '', label: 'None (top-level)' }, ...parentOptions]}
            placeholder="Top-level"
          />
        </label>
      </div>
      <div className="col-span-2">
        <button
          type="button"
          onClick={() => set('is_active', !draft.is_active)}
          aria-pressed={draft.is_active}
          className={clsx(
            'text-2xs px-2 py-0.5 rounded flex items-center gap-1',
            draft.is_active ? 'bg-success/15 text-success' : 'bg-surface-4 text-ink-muted'
          )}
        >
          <Check size={10} /> {draft.is_active ? 'Active' : 'Inactive'}
        </button>
      </div>
    </div>
  );
}

// ── Category → Marketplace mappings (Day 98.5c) ─────────────────────────────

/** A single category↔marketplace external-taxonomy mapping row. */
interface MappingRow {
  id: string;
  category_id: string;
  marketplace_id: string;
  external_id: string | null;
  external_path: string | null;
  external_name: string | null;
}

/** Marketplace picker option — mirrors BrandsSection's shape (label = display_name). */
interface MarketplaceOption {
  id: string;
  channel: string;
  label: string;
}

interface MappingDraft {
  marketplace_id: string;
  external_id: string;
  external_path: string;
  external_name: string;
}

const EMPTY_MAPPING_DRAFT: MappingDraft = {
  marketplace_id: '',
  external_id: '',
  external_path: '',
  external_name: '',
};

interface CategoryMappingsPanelProps {
  categoryId: string;
  categoryName: string;
  mappings: MappingRow[];
  marketplaces: MarketplaceOption[];
  isAdmin: boolean;
  showToast: (t: ToastState) => void;
  onChanged: () => Promise<void>;
}

/**
 * Per-category marketplace-mapping sub-surface (Day 98.5c). Lists a category's
 * mappings as a plain nested table (div-grid — Directory uses no AG Grid) with
 * admin-gated add/edit (a dedicated small form; ArbiSelect marketplace picker
 * labelled by display_name) and ConfirmDialog-gated delete. Eager per-action
 * save; the parent reloads via onChanged. Delete is scoped to MAPPING rows only —
 * category-entity delete stays retired (Day 64 A.2; categories has no DELETE policy).
 */
function CategoryMappingsPanel({
  categoryId,
  categoryName,
  mappings,
  marketplaces,
  isAdmin,
  showToast,
  onChanged,
}: CategoryMappingsPanelProps): JSX.Element {
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [addDraft, setAddDraft] = useState<MappingDraft>(EMPTY_MAPPING_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<MappingDraft>(EMPTY_MAPPING_DRAFT);
  const [confirmDelete, setConfirmDelete] = useState<MappingRow | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const fieldId = useId();
  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';
  const inputCls = 'arbi-input w-full text-xs px-2 py-1';
  const gridCols = 'grid grid-cols-[1.2fr_1fr_1.4fr_1.4fr_auto] gap-2';

  const mpLabel = useCallback(
    (mid: string): string => marketplaces.find((m) => m.id === mid)?.label ?? 'Unknown marketplace',
    [marketplaces]
  );

  // Add picker excludes marketplaces already mapped for this category (the
  // UNIQUE(category_id, marketplace_id) guard); a stale-race dup still surfaces
  // via the 23505 toast below.
  const mappedIds = useMemo(() => new Set(mappings.map((m) => m.marketplace_id)), [mappings]);
  const addOptions = useMemo(
    () =>
      marketplaces
        .filter((m) => !mappedIds.has(m.id))
        .map((m) => ({ value: m.id, label: m.label })),
    [marketplaces, mappedIds]
  );

  function resetAdd(): void {
    setAddOpen(false);
    setAddDraft(EMPTY_MAPPING_DRAFT);
  }

  async function handleAdd(): Promise<void> {
    if (!addDraft.marketplace_id) {
      showToast({ kind: 'error', message: 'Select a marketplace' });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('category_marketplace_mappings').insert({
      category_id: categoryId,
      marketplace_id: addDraft.marketplace_id,
      external_id: addDraft.external_id.trim() || null,
      external_path: addDraft.external_path.trim() || null,
      external_name: addDraft.external_name.trim() || null,
    } as never);
    setSaving(false);
    if (error) {
      // 23505 = unique_violation: this (category, marketplace) pair already exists.
      const dup = error.code === '23505';
      showToast({
        kind: 'error',
        message: dup
          ? `${mpLabel(addDraft.marketplace_id)} is already mapped for ${categoryName}`
          : `Save failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: 'Mapping added' });
    resetAdd();
    await onChanged();
  }

  function beginEdit(m: MappingRow): void {
    setAddOpen(false);
    setEditingId(m.id);
    setEditDraft({
      marketplace_id: m.marketplace_id,
      external_id: m.external_id ?? '',
      external_path: m.external_path ?? '',
      external_name: m.external_name ?? '',
    });
  }

  async function saveEdit(m: MappingRow): Promise<void> {
    setSaving(true);
    // Marketplace is fixed on edit (changing it is delete+add) — only external_* change,
    // so the UNIQUE pair never moves under us.
    const { error } = await supabase
      .from('category_marketplace_mappings')
      .update({
        external_id: editDraft.external_id.trim() || null,
        external_path: editDraft.external_path.trim() || null,
        external_name: editDraft.external_name.trim() || null,
      } as never)
      .eq('id', m.id);
    setSaving(false);
    if (error) {
      showToast({
        kind: 'error',
        message: `Save failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: 'Mapping updated' });
    setEditingId(null);
    await onChanged();
  }

  async function handleDelete(m: MappingRow): Promise<void> {
    const { error } = await supabase.from('category_marketplace_mappings').delete().eq('id', m.id);
    setConfirmDelete(null);
    if (error) {
      showToast({
        kind: 'error',
        message: `Delete failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: 'Mapping removed' });
    await onChanged();
  }

  return (
    <div className="mt-2 ml-6 border-l-2 border-border-subtle pl-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-2xs uppercase tracking-wider text-ink-muted">
          Marketplace mappings ({mappings.length})
        </span>
        {isAdmin && addOptions.length > 0 && (
          <button
            onClick={() => {
              setAddOpen((v) => !v);
              setAddDraft(EMPTY_MAPPING_DRAFT);
            }}
            className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-2 py-0.5 flex items-center gap-1"
          >
            <Plus size={10} /> {addOpen ? 'Cancel' : 'Add mapping'}
          </button>
        )}
      </div>

      {addOpen && isAdmin && (
        <div className="mb-2 bg-surface-2 border border-accent/30 rounded-lg p-2.5 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>
                Marketplace
                <ArbiSelect
                  value={addDraft.marketplace_id || null}
                  options={addOptions}
                  onChange={(v) => setAddDraft({ ...addDraft, marketplace_id: v })}
                  placeholder="Select marketplace…"
                />
              </label>
            </div>
            <div>
              <label htmlFor={`${fieldId}-add-id`} className={labelCls}>
                External ID
              </label>
              <input
                id={`${fieldId}-add-id`}
                value={addDraft.external_id}
                onChange={(e) => setAddDraft({ ...addDraft, external_id: e.target.value })}
                className={`${inputCls} font-mono`}
              />
            </div>
            <div>
              <label htmlFor={`${fieldId}-add-path`} className={labelCls}>
                External path
              </label>
              <input
                id={`${fieldId}-add-path`}
                value={addDraft.external_path}
                onChange={(e) => setAddDraft({ ...addDraft, external_path: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor={`${fieldId}-add-name`} className={labelCls}>
                External name
              </label>
              <input
                id={`${fieldId}-add-name`}
                value={addDraft.external_name}
                onChange={(e) => setAddDraft({ ...addDraft, external_name: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={resetAdd}
              className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-2.5 py-0.5"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="arbi-btn text-2xs bg-accent text-white hover:bg-accent-hover px-2.5 py-0.5 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {mappings.length === 0 ? (
        <p className="text-2xs text-ink-muted py-1.5">No marketplace mappings.</p>
      ) : (
        <div className="divide-y divide-border-subtle">
          <div className={`${gridCols} pb-1 text-2xs uppercase tracking-wider text-ink-muted`}>
            <span>Marketplace</span>
            <span>External ID</span>
            <span>External path</span>
            <span>External name</span>
            <span aria-hidden="true" />
          </div>
          {mappings.map((m) => {
            if (editingId === m.id && isAdmin) {
              return (
                <div key={m.id} className="py-2 space-y-2">
                  <div className="grid grid-cols-[1.2fr_1fr_1.4fr_1.4fr] gap-2 items-center">
                    <span className="text-xs text-ink-primary">{mpLabel(m.marketplace_id)}</span>
                    <input
                      value={editDraft.external_id}
                      onChange={(e) => setEditDraft({ ...editDraft, external_id: e.target.value })}
                      className={`${inputCls} font-mono`}
                      aria-label="External ID"
                    />
                    <input
                      value={editDraft.external_path}
                      onChange={(e) =>
                        setEditDraft({ ...editDraft, external_path: e.target.value })
                      }
                      className={inputCls}
                      aria-label="External path"
                    />
                    <input
                      value={editDraft.external_name}
                      onChange={(e) =>
                        setEditDraft({ ...editDraft, external_name: e.target.value })
                      }
                      className={inputCls}
                      aria-label="External name"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingId(null)}
                      className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-2.5 py-0.5"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveEdit(m)}
                      disabled={saving}
                      className="arbi-btn text-2xs bg-accent text-white hover:bg-accent-hover px-2.5 py-0.5 flex items-center gap-1 disabled:opacity-50"
                    >
                      <Save size={10} /> Save
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className={`${gridCols} py-1.5 items-center text-xs`}>
                <span className="text-ink-primary truncate">{mpLabel(m.marketplace_id)}</span>
                <span className="text-ink-secondary font-mono truncate">
                  {m.external_id || '—'}
                </span>
                <span className="text-ink-secondary truncate">{m.external_path || '—'}</span>
                <span className="text-ink-secondary truncate">{m.external_name || '—'}</span>
                <span className="flex items-center gap-2 justify-end text-ink-muted">
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => beginEdit(m)}
                        className="hover:text-ink-bright"
                        aria-label={`Edit ${mpLabel(m.marketplace_id)} mapping`}
                      >
                        <Pencil size={10} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(m)}
                        className="hover:text-danger"
                        aria-label={`Delete ${mpLabel(m.marketplace_id)} mapping`}
                      >
                        <Trash2 size={10} />
                      </button>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove mapping"
        message={
          confirmDelete
            ? `The ${mpLabel(confirmDelete.marketplace_id)} mapping for ${categoryName} will be removed. This cannot be undone.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

interface CategoryMappingImportPanelProps {
  categories: CategoryRow[];
  marketplaces: MarketplaceOption[];
  showToast: (t: ToastState) => void;
  onImported: () => Promise<void>;
  onClose: () => void;
}

/**
 * Day 98.5c — SheetJS bulk-import panel for category↔marketplace mappings
 * (admin-only; the upsert needs is_admin() for the INSERT and the on-conflict
 * UPDATE). Mirrors the Files.tsx brand bulk-create flow (file → XLSX parse →
 * client-side {ready, unresolved} preview) but DEVIATES at commit: it upserts
 * on the UNIQUE(category_id, marketplace_id) so a re-import REFRESHES external_*
 * on an existing pair instead of skipping it. Parsing + upsert live here; the
 * pure name/channel → FK resolution is resolveMappingRows.
 */
function CategoryMappingImportPanel({
  categories,
  marketplaces,
  showToast,
  onImported,
  onClose,
}: CategoryMappingImportPanelProps): JSX.Element {
  const [fileName, setFileName] = useState<string>('');
  const [preview, setPreview] = useState<ResolveResult | null>(null);
  const [parsing, setParsing] = useState<boolean>(false);
  const [importing, setImporting] = useState<boolean>(false);

  // Lookups reuse CategoriesSection's already-loaded reference data (no re-fetch).
  const categoryLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) if (c.name) map.set(normalizeKey(c.name), c.id);
    return map;
  }, [categories]);
  const channelLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of marketplaces) map.set(normalizeKey(m.channel), m.id);
    return map;
  }, [marketplaces]);

  async function handleFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setPreview(null);
    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
      const sheetName = workbook.SheetNames[0];
      const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
      if (!sheet) throw new Error('No sheet found in the file');
      // Plain known-column sheet (header row 1) — object mode, NOT the Algopix
      // header:1 form (CLAUDE.md §13 caveat is Algopix-specific).
      const dataRows = XLSX.utils.sheet_to_json(sheet, { defval: null }) as Record<
        string,
        unknown
      >[];
      const rawRows: RawMappingRow[] = [];
      dataRows.forEach((obj, i) => {
        const norm: Record<string, unknown> = {};
        for (const k of Object.keys(obj)) norm[k.trim().toLowerCase()] = obj[k];
        const cell = (key: string): string => {
          const v = norm[key];
          return v == null ? '' : String(v).trim();
        };
        const category = cell('category');
        const channel = cell('channel');
        const external_id = cell('external_id');
        const external_path = cell('external_path');
        const external_name = cell('external_name');
        // Skip fully-blank rows (trailing spreadsheet rows); rowNumber is the
        // 1-based source row (data starts at row 2 after the header).
        if (!category && !channel && !external_id && !external_path && !external_name) return;
        rawRows.push({
          rowNumber: i + 2,
          category,
          channel,
          external_id,
          external_path,
          external_name,
        });
      });
      if (rawRows.length === 0) {
        showToast({
          kind: 'error',
          message:
            'No data rows found. Expected columns: category, channel, external_id, external_path, external_name.',
        });
        return;
      }
      setPreview(resolveMappingRows(rawRows, categoryLookup, channelLookup));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast({ kind: 'error', message: `Import parse failed — ${msg}` });
    } finally {
      setParsing(false);
      input.value = ''; // allow re-selecting the same file
    }
  }

  async function commitImport(): Promise<void> {
    if (!preview || preview.ready.length === 0) return;
    setImporting(true);
    // DEVIATION from the brand flow: upsert-on-conflict refreshes external_* on an
    // existing (category, marketplace) pair rather than insert-after-filter.
    const { error } = await supabase
      .from('category_marketplace_mappings')
      .upsert(preview.ready as never, { onConflict: 'category_id,marketplace_id' });
    setImporting(false);
    if (error) {
      showToast({
        kind: 'error',
        message: `Import failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    const n = preview.ready.length;
    showToast({ kind: 'success', message: `${n} mapping${n === 1 ? '' : 's'} imported` });
    setPreview(null);
    setFileName('');
    await onImported();
    onClose();
  }

  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';

  return (
    <div className="mb-4 bg-surface-2 border border-accent/30 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-bright">Import marketplace mappings</span>
        <button
          onClick={onClose}
          className="text-ink-muted hover:text-ink-bright"
          aria-label="Close import"
        >
          <X size={14} />
        </button>
      </div>
      <p className="text-2xs text-ink-muted">
        Columns:{' '}
        <span className="font-mono">
          category, channel, external_id, external_path, external_name
        </span>
        . Category matches by name; channel by its marketplace key. Existing (category, marketplace)
        pairs are refreshed.
      </p>
      <div>
        <label htmlFor="mapping-import-file" className={labelCls}>
          File (.xlsx / .csv)
        </label>
        <input
          id="mapping-import-file"
          type="file"
          accept=".xlsx,.csv"
          onChange={handleFile}
          className="text-2xs text-ink-secondary file:mr-2 file:rounded file:border-0 file:bg-surface-4 file:px-2 file:py-1 file:text-ink-secondary hover:file:text-ink-bright"
        />
      </div>

      {parsing && <p className="text-2xs text-ink-muted">Parsing {fileName}…</p>}

      {preview && (
        <div className="space-y-2">
          <p className="text-xs text-ink-primary">
            <span className="text-success font-medium">{preview.ready.length} ready</span>
            {' · '}
            <span
              className={
                preview.unresolved.length > 0 ? 'text-warning font-medium' : 'text-ink-muted'
              }
            >
              {preview.unresolved.length} skipped
            </span>
          </p>
          {preview.unresolved.length > 0 && (
            <div className="max-h-40 overflow-y-auto border border-border-subtle rounded divide-y divide-border-subtle">
              {preview.unresolved.map((u) => (
                <div
                  key={u.rowNumber}
                  className="px-2 py-1 text-2xs flex items-center justify-between gap-2"
                >
                  <span className="text-ink-secondary truncate">
                    Row {u.rowNumber}: {u.category || '(blank)'} / {u.channel || '(blank)'}
                  </span>
                  <span className="text-warning shrink-0">
                    {u.reason === 'category-not-found'
                      ? 'Category name not found'
                      : 'Unknown channel'}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={commitImport}
              disabled={preview.ready.length === 0 || importing}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 disabled:opacity-50"
            >
              Import {preview.ready.length} mapping{preview.ready.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoriesSection({ sectionRefs, showToast, isAdmin }: SectionProps): JSX.Element {
  // Day 64 A.2: Categories Delete UI removed — no DELETE policy on categories;
  // legacy affordance silently failed in prod. Soft-delete via is_active stays
  // editable. handleDelete + confirmDelete state + Delete button + ConfirmDialog
  // all retired this session. Edit + Add affordances retained.
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [marketplaces, setMarketplaces] = useState<MarketplaceOption[]>([]);
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newDraft, setNewDraft] = useState<CategoryDraft>(EMPTY_CATEGORY_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<CategoryDraft>(EMPTY_CATEGORY_DRAFT);

  // Day 98.5c — category↔marketplace mappings load alongside the categories list.
  // marketplaces fetch mirrors BrandsSection (NOT useSettingsConfig — its
  // marketplaces export was retired Day 63a).
  const loadMappings = useCallback(async (): Promise<void> => {
    const { data } = await supabase
      .from('category_marketplace_mappings')
      .select('id, category_id, marketplace_id, external_id, external_path, external_name');
    setMappings((data as unknown as MappingRow[]) || []);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    const [catRes, mpRes] = await Promise.all([
      supabase.from('categories').select('*').order('name'),
      supabase.from('marketplaces').select('id, channel, display_name').order('display_name'),
    ]);
    setCategories((catRes.data as unknown as CategoryRow[]) || []);
    setMarketplaces(
      (
        (mpRes.data as unknown as Array<{ id: string; channel: string; display_name: string }>) ||
        []
      ).map((m) => ({ id: m.id, channel: m.channel, label: m.display_name }))
    );
    await loadMappings();
    setLoading(false);
  }, [loadMappings]);

  useEffect(() => {
    load();
  }, [load]);

  const mappingsByCat = useMemo(() => {
    const map = new Map<string, MappingRow[]>();
    for (const m of mappings) {
      const arr = map.get(m.category_id);
      if (arr) arr.push(m);
      else map.set(m.category_id, [m]);
    }
    return map;
  }, [mappings]);

  const toggleExpand = (cid: string): void =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });

  const getParentName = (parentId: string): string =>
    categories.find((c) => c.id === parentId)?.name || '';

  // Parent options exclude the current row (can't be its own parent).
  const parentOptionsForId = useCallback(
    (excludeId: string | null): Array<{ value: string; label: string }> =>
      categories
        .filter((c) => c.id !== excludeId)
        .map((c) => ({ value: c.id, label: c.name ?? '' })),
    [categories]
  );

  async function handleAdd(): Promise<void> {
    const payload = buildCategoryPayload(newDraft);
    if ('error' in payload) {
      showToast({ kind: 'error', message: String(payload.error) });
      return;
    }
    const { error } = await supabase.from('categories').insert(payload as never);
    if (error) {
      showToast({
        kind: 'error',
        message: `Save failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: `${payload.name as string} added` });
    setShowAddForm(false);
    setNewDraft(EMPTY_CATEGORY_DRAFT);
    await load();
  }

  function beginEdit(c: CategoryRow): void {
    setEditingId(c.id);
    setEditDraft(categoryToDraft(c));
  }

  async function saveEdit(c: CategoryRow): Promise<void> {
    const payload = buildCategoryPayload(editDraft);
    if ('error' in payload) {
      showToast({ kind: 'error', message: String(payload.error) });
      return;
    }
    const { error } = await supabase
      .from('categories')
      .update(payload as never)
      .eq('id', c.id);
    if (error) {
      showToast({
        kind: 'error',
        message: `Save failed — ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: `${payload.name as string} updated` });
    setEditingId(null);
    await load();
  }

  const addAction = isAdmin ? (
    <div className="flex items-center gap-2">
      <button
        onClick={() => {
          setImportOpen((v) => !v);
          setShowAddForm(false);
        }}
        className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
      >
        <Upload size={10} /> {importOpen ? 'Cancel' : 'Import Mappings'}
      </button>
      <button
        onClick={() => {
          setShowAddForm((v) => !v);
          setNewDraft(EMPTY_CATEGORY_DRAFT);
          setImportOpen(false);
        }}
        className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
      >
        <Plus size={10} /> {showAddForm ? 'Cancel' : 'Add Category'}
      </button>
    </div>
  ) : null;

  return (
    <SectionCard id="categories" title="Categories" sectionRefs={sectionRefs} action={addAction}>
      {importOpen && isAdmin && (
        <CategoryMappingImportPanel
          categories={categories}
          marketplaces={marketplaces}
          showToast={showToast}
          onImported={loadMappings}
          onClose={() => setImportOpen(false)}
        />
      )}
      {showAddForm && isAdmin && (
        <div className="mb-4 bg-surface-2 border border-accent/30 rounded-lg p-3 space-y-3">
          <CategoryForm
            draft={newDraft}
            onChange={setNewDraft}
            parentOptions={parentOptionsForId(null)}
          />
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewDraft(EMPTY_CATEGORY_DRAFT);
              }}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1"
            >
              Save Category
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-ink-muted">Loading...</p>
      ) : categories.length === 0 ? (
        <div className="py-8 text-center">
          <FolderTree size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">No categories yet</p>
        </div>
      ) : (
        <div className="divide-y divide-border-subtle">
          {categories.map((c) => {
            const isEditing = editingId === c.id;
            return (
              <div key={c.id} className="py-2.5 px-1">
                {isEditing && isAdmin ? (
                  <div className="space-y-3">
                    <CategoryForm
                      draft={editDraft}
                      onChange={setEditDraft}
                      parentOptions={parentOptionsForId(c.id)}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingId(null)}
                        className="arbi-btn text-xs bg-surface-4 text-ink-secondary px-3 py-1"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => saveEdit(c)}
                        className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 flex items-center gap-1"
                      >
                        <Save size={10} /> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className={clsx('flex items-center justify-between', c.parent_id && 'pl-6')}
                    >
                      <span className="text-sm text-ink-primary">
                        {c.parent_id && (
                          <span className="text-ink-muted">
                            {getParentName(c.parent_id)} &rsaquo;{' '}
                          </span>
                        )}
                        {c.name}
                      </span>
                      <div className="flex items-center gap-3 text-xs text-ink-secondary">
                        <button
                          onClick={() => toggleExpand(c.id)}
                          className="text-ink-muted hover:text-ink-bright flex items-center gap-1"
                          aria-expanded={expandedIds.has(c.id)}
                          aria-label={`${expandedIds.has(c.id) ? 'Hide' : 'Show'} marketplace mappings for ${c.name ?? 'category'}`}
                        >
                          {expandedIds.has(c.id) ? (
                            <ChevronDown size={10} />
                          ) : (
                            <ChevronRight size={10} />
                          )}
                          Mappings ({mappingsByCat.get(c.id)?.length ?? 0})
                        </button>
                        <span
                          className={clsx(
                            'px-1.5 py-0.5 rounded text-2xs font-medium',
                            c.is_active
                              ? 'bg-success/15 text-success'
                              : 'bg-surface-3 text-ink-muted'
                          )}
                        >
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {isAdmin && (
                          <button
                            onClick={() => beginEdit(c)}
                            className="text-ink-muted hover:text-ink-bright flex items-center gap-1"
                            aria-label={`Edit ${c.name ?? 'category'}`}
                          >
                            <Pencil size={10} /> Edit
                          </button>
                        )}
                      </div>
                    </div>
                    {expandedIds.has(c.id) && (
                      <CategoryMappingsPanel
                        categoryId={c.id}
                        categoryName={c.name ?? 'this category'}
                        mappings={mappingsByCat.get(c.id) ?? []}
                        marketplaces={marketplaces}
                        isAdmin={isAdmin}
                        showToast={showToast}
                        onChanged={loadMappings}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// ── Marketplaces section ──────────────────────────────────────────────────

// Day 86 P7.2 + A1 arm b — MarketplaceDraft slimmed: is_active editing
// relocated to Settings → Channels & Paths. MarketplaceForm owns identity +
// fees + connection metadata only. Closes Audit #2 Finding 6 dual-write.
interface MarketplaceDraft {
  channel: string;
  display_name: string;
  referral_fee_pct: string;
  fba_fee_per_unit: string;
  wfs_fee_per_unit: string;
  payout_cycle_days: string;
  listing_fee: string;
  currency: string;
}

const EMPTY_MARKETPLACE_DRAFT: MarketplaceDraft = {
  channel: '',
  display_name: '',
  referral_fee_pct: '',
  fba_fee_per_unit: '',
  wfs_fee_per_unit: '',
  payout_cycle_days: '',
  listing_fee: '',
  currency: 'USD',
};

function marketplaceToDraft(m: MarketplaceRow): MarketplaceDraft {
  return {
    channel: m.channel ?? '',
    display_name: m.display_name ?? '',
    referral_fee_pct: m.referral_fee_pct != null ? String(m.referral_fee_pct) : '',
    fba_fee_per_unit: m.fba_fee_per_unit != null ? String(m.fba_fee_per_unit) : '',
    wfs_fee_per_unit: m.wfs_fee_per_unit != null ? String(m.wfs_fee_per_unit) : '',
    payout_cycle_days: m.payout_cycle_days != null ? String(m.payout_cycle_days) : '',
    listing_fee: m.listing_fee != null ? String(m.listing_fee) : '',
    currency: m.currency ?? 'USD',
  };
}

function buildMarketplacePayload(d: MarketplaceDraft): Record<string, unknown> | { error: string } {
  const channel = d.channel.trim().toLowerCase();
  const displayName = d.display_name.trim();
  if (!channel) return { error: 'Channel identifier is required' };
  if (!displayName) return { error: 'Display name is required' };

  const referralStr = d.referral_fee_pct.trim();
  let referral: number | null = null;
  if (referralStr) {
    const n = Number(referralStr);
    if (!Number.isFinite(n) || n < 0 || n > 100)
      return { error: 'Referral fee must be between 0 and 100' };
    referral = n;
  }

  const cycleStr = d.payout_cycle_days.trim();
  let cycle: number | null = null;
  if (cycleStr) {
    const n = Number(cycleStr);
    if (!Number.isFinite(n) || n < 0) return { error: 'Payout cycle must be 0 or greater' };
    cycle = n;
  }

  const numOrNull = (s: string): number | null => {
    const t = s.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : NaN;
  };
  const fba = numOrNull(d.fba_fee_per_unit);
  if (Number.isNaN(fba)) return { error: 'FBA fee must be a number' };
  const wfs = numOrNull(d.wfs_fee_per_unit);
  if (Number.isNaN(wfs)) return { error: 'WFS fee must be a number' };
  const listing = numOrNull(d.listing_fee);
  if (Number.isNaN(listing)) return { error: 'Listing fee must be a number' };

  return {
    channel,
    display_name: displayName,
    // Day 86 P7.2 + A1 arm b — is_active omitted from payload; column persists
    // at current value. Channels & Paths Settings owns the toggle.
    referral_fee_pct: referral,
    fba_fee_per_unit: fba,
    wfs_fee_per_unit: wfs,
    payout_cycle_days: cycle,
    listing_fee: listing,
    currency: d.currency,
  };
}

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'CAD', label: 'CAD' },
  { value: 'GBP', label: 'GBP' },
  { value: 'EUR', label: 'EUR' },
  { value: 'AUD', label: 'AUD' },
];

interface MarketplaceFormProps {
  draft: MarketplaceDraft;
  onChange: (d: MarketplaceDraft) => void;
  channelEditable: boolean;
}

function MarketplaceForm({ draft, onChange, channelEditable }: MarketplaceFormProps): JSX.Element {
  const set = <K extends keyof MarketplaceDraft>(k: K, v: MarketplaceDraft[K]): void =>
    onChange({ ...draft, [k]: v });
  const ch = draft.channel.toLowerCase();
  const showFba = ch.includes('fba');
  const showWfs = ch.includes('wfs') || ch.includes('walmart');
  const id = useId();
  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label htmlFor={`${id}-channel`} className={labelCls}>
          Channel ID
        </label>
        <input
          id={`${id}-channel`}
          value={draft.channel}
          onChange={(e) => set('channel', e.target.value)}
          disabled={!channelEditable}
          className={clsx(
            'arbi-input w-full text-xs px-2 py-1 font-mono',
            !channelEditable && 'opacity-60 cursor-not-allowed'
          )}
          placeholder="amazon_fba"
        />
        {!channelEditable && (
          <p className="text-2xs text-ink-muted mt-1">Channel ID is immutable after create</p>
        )}
      </div>
      <div>
        <label htmlFor={`${id}-display`} className={labelCls}>
          Display name
        </label>
        <input
          id={`${id}-display`}
          value={draft.display_name}
          onChange={(e) => set('display_name', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1"
          placeholder="Amazon FBA"
        />
      </div>
      {/* Fix Pass A (S-F footgun) — the marketplace fee/payout columns are
          reference only; the profit engine reads channel fees from params
          (ebay/walmart/shopify) + product columns (amazon), NOT these rows.
          Labeled so an edit here can't be mistaken for changing profit math. */}
      <p className="col-span-2 -mb-1 text-2xs text-ink-muted">
        Fee &amp; payout fields below are reference only — profit calculations use params
        (eBay/Walmart/Shopify) and Amazon&apos;s live SP-API/Keepa fees, not these values.
      </p>
      <div>
        <label htmlFor={`${id}-referral`} className={labelCls}>
          Referral fee %
        </label>
        <input
          id={`${id}-referral`}
          type="number"
          step="0.01"
          value={draft.referral_fee_pct}
          onChange={(e) => set('referral_fee_pct', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1 font-mono"
          placeholder="0-100"
        />
      </div>
      <div>
        <label htmlFor={`${id}-listing-fee`} className={labelCls}>
          Listing fee $
        </label>
        <input
          id={`${id}-listing-fee`}
          type="number"
          step="0.0001"
          value={draft.listing_fee}
          onChange={(e) => set('listing_fee', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1 font-mono"
        />
      </div>
      {showFba && (
        <div>
          <label htmlFor={`${id}-fba-fee`} className={labelCls}>
            FBA fee per unit $
          </label>
          <input
            id={`${id}-fba-fee`}
            type="number"
            step="0.0001"
            value={draft.fba_fee_per_unit}
            onChange={(e) => set('fba_fee_per_unit', e.target.value)}
            className="arbi-input w-full text-xs px-2 py-1 font-mono"
          />
        </div>
      )}
      {showWfs && (
        <div>
          <label htmlFor={`${id}-wfs-fee`} className={labelCls}>
            WFS fee per unit $
          </label>
          <input
            id={`${id}-wfs-fee`}
            type="number"
            step="0.0001"
            value={draft.wfs_fee_per_unit}
            onChange={(e) => set('wfs_fee_per_unit', e.target.value)}
            className="arbi-input w-full text-xs px-2 py-1 font-mono"
          />
        </div>
      )}
      <div>
        <label htmlFor={`${id}-payout-cycle`} className={labelCls}>
          Payout cycle (days)
        </label>
        <input
          id={`${id}-payout-cycle`}
          type="number"
          value={draft.payout_cycle_days}
          onChange={(e) => set('payout_cycle_days', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1 font-mono"
        />
      </div>
      <div>
        <label className={labelCls}>
          Currency
          <ArbiSelect
            value={draft.currency}
            onChange={(v) => set('currency', v)}
            options={CURRENCY_OPTIONS}
          />
        </label>
      </div>
      {/* Day 86 P7.2 + A1 arm b — is_active toggle button + dual-write caption
          REMOVED. Channel-level activation now lives at Settings → Channels &
          Paths (single-door). Closes Audit #2 Finding 6 dual-write surface. */}
    </div>
  );
}

const MARKETPLACE_STATUS_BADGE: Record<string, string> = {
  connected: 'bg-success/15 text-success',
  error: 'bg-danger/15 text-danger',
  not_connected: 'bg-surface-3 text-ink-muted',
};

function MarketplacesSection({ sectionRefs, showToast, isAdmin }: SectionProps): JSX.Element {
  // Day 64 A.3: Marketplaces Add + Delete UI removed — marketplaces table has
  // neither INSERT nor DELETE policy (soft-delete-only architecture per ADR 34 +
  // Day 63a graduation). Edit affordances retained — marketplaces_update_admin
  // policy still allows admin to edit identity / fees / connection metadata.
  //
  // Day 86 P7.2 + A1 arm b: is_active toggle relocated from MarketplaceForm
  // (was at L4407-4421 with dual-write caption acknowledging arbi-dev
  // FormulaEditor as parallel writer) to arbi-root Settings → Channels & Paths.
  // Single-door semantic — MarketplaceForm now owns identity + fees +
  // connection metadata only. Closes Audit #2 Finding 6.
  const [marketplaces, setMarketplaces] = useState<MarketplaceRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<MarketplaceDraft>(EMPTY_MARKETPLACE_DRAFT);

  const load = useCallback(async (): Promise<void> => {
    const { data } = await supabase.from('marketplaces').select('*').order('display_name');
    setMarketplaces((data as unknown as MarketplaceRow[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function beginEdit(m: MarketplaceRow): void {
    setEditingId(m.id);
    setEditDraft(marketplaceToDraft(m));
  }

  async function saveEdit(m: MarketplaceRow): Promise<void> {
    const payload = buildMarketplacePayload(editDraft);
    if ('error' in payload) {
      showToast({ kind: 'error', message: String(payload.error) });
      return;
    }
    // Channel is immutable after create - strip from update payload.
    const { channel: _omit, ...rest } = payload;
    void _omit;
    const { error } = await supabase
      .from('marketplaces')
      .update(rest as never)
      .eq('id', m.id);
    if (error) {
      showToast({
        kind: 'error',
        message: `Save failed - ${translatePostgrestError(error).message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: `${payload.display_name as string} updated` });
    setEditingId(null);
    await load();
  }

  return (
    <SectionCard id="marketplaces" title="Marketplaces" sectionRefs={sectionRefs}>
      {loading ? (
        <p className="py-8 text-center text-sm text-ink-muted">Loading...</p>
      ) : marketplaces.length === 0 ? (
        <div className="py-8 text-center">
          <Globe size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">No marketplaces configured yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {marketplaces.map((m) => {
            return (
              <div key={m.id} className="bg-surface-2 border border-border-subtle rounded-lg p-4">
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-ink-bright truncate">
                        {m.display_name}
                      </span>
                    </div>
                    <span
                      className={clsx(
                        'px-1.5 py-0.5 rounded text-2xs font-medium capitalize',
                        (m.api_connection_status &&
                          MARKETPLACE_STATUS_BADGE[m.api_connection_status]) ||
                          MARKETPLACE_STATUS_BADGE.not_connected
                      )}
                    >
                      {(m.api_connection_status || 'not_connected').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Channel</span>
                      <span className="font-mono text-ink-primary">{m.channel || '\u2014'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Referral fee</span>
                      <span className="font-mono text-ink-primary">
                        {m.referral_fee_pct != null ? `${m.referral_fee_pct}%` : '\u2014'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Currency / payout</span>
                      <span className="font-mono text-ink-primary">
                        {m.currency || 'USD'}
                        {m.payout_cycle_days != null ? ` \u00b7 ${m.payout_cycle_days}d` : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Seller ID</span>
                      <span className="font-mono text-ink-primary">{m.seller_id || '\u2014'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Account</span>
                      <span className="text-ink-primary">{m.seller_account_name || '\u2014'}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex justify-end gap-3 mt-3 text-xs">
                      <button
                        onClick={() => beginEdit(m)}
                        className="text-ink-muted hover:text-ink-bright flex items-center gap-1"
                        aria-label={`Edit ${m.display_name ?? 'marketplace'}`}
                      >
                        <Pencil size={10} /> Edit
                      </button>
                    </div>
                  )}
                </>
              </div>
            );
          })}
        </div>
      )}
      <FormModal
        open={editingId !== null && isAdmin}
        title="Edit marketplace"
        size="xl"
        onClose={() => setEditingId(null)}
        footer={
          <>
            <button
              onClick={() => setEditingId(null)}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const m = marketplaces.find((x) => x.id === editingId);
                if (m) void saveEdit(m);
              }}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 flex items-center gap-1"
            >
              <Save size={10} /> Save
            </button>
          </>
        }
      >
        <MarketplaceForm draft={editDraft} onChange={setEditDraft} channelEditable={false} />
      </FormModal>
    </SectionCard>
  );
}

// ── Warehouses section ────────────────────────────────────────────────────

interface WarehouseDraft {
  name: string;
  code: string;
  address: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  notes: string;
  is_active: boolean;
}

const EMPTY_WAREHOUSE_DRAFT: WarehouseDraft = {
  name: '',
  code: '',
  address: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  notes: '',
  is_active: true,
};

function warehouseToDraft(w: WarehouseRow): WarehouseDraft {
  return {
    name: w.name ?? '',
    code: w.code ?? '',
    address: w.address ?? '',
    contact_name: w.contact_name ?? '',
    contact_email: w.contact_email ?? '',
    contact_phone: w.contact_phone ?? '',
    notes: w.notes ?? '',
    is_active: w.is_active ?? true,
  };
}

function buildWarehousePayload(d: WarehouseDraft): Record<string, unknown> | { error: string } {
  const name = d.name.trim();
  const code = d.code.trim();
  if (!name) return { error: 'Warehouse name is required' };
  if (!code) return { error: 'Warehouse code is required' };
  return {
    name,
    code,
    address: d.address.trim() || null,
    contact_name: d.contact_name.trim() || null,
    contact_email: d.contact_email.trim() || null,
    contact_phone: d.contact_phone.trim() || null,
    notes: d.notes.trim() || null,
    is_active: d.is_active,
  };
}

interface WarehouseFormProps {
  draft: WarehouseDraft;
  onChange: (d: WarehouseDraft) => void;
  // Day 84.6 P5 — translator output for inline server-error rendering below code input.
  serverFieldErrors?: UserError[];
}

function WarehouseForm({ draft, onChange, serverFieldErrors }: WarehouseFormProps): JSX.Element {
  const set = <K extends keyof WarehouseDraft>(k: K, v: WarehouseDraft[K]): void =>
    onChange({ ...draft, [k]: v });
  const id = useId();
  const labelCls = 'text-2xs uppercase tracking-wider text-ink-muted block mb-1';
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label htmlFor={`${id}-name`} className={labelCls}>
          Name
        </label>
        <input
          id={`${id}-name`}
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1"
          placeholder="Vegas Main"
        />
      </div>
      <div>
        <label htmlFor={`${id}-code`} className={labelCls}>
          Code (unique)
        </label>
        <input
          id={`${id}-code`}
          value={draft.code}
          onChange={(e) => set('code', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1 font-mono"
          placeholder="VGS-MAIN"
        />
        <FieldError fieldPath="code" userErrors={serverFieldErrors ?? []} />
      </div>
      <div className="col-span-2">
        <label htmlFor={`${id}-address`} className={labelCls}>
          Address
        </label>
        <textarea
          id={`${id}-address`}
          value={draft.address}
          onChange={(e) => set('address', e.target.value)}
          rows={2}
          className="arbi-input w-full text-xs px-2 py-1"
        />
      </div>
      <div>
        <label htmlFor={`${id}-contact-name`} className={labelCls}>
          Contact name
        </label>
        <input
          id={`${id}-contact-name`}
          value={draft.contact_name}
          onChange={(e) => set('contact_name', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1"
        />
      </div>
      <div>
        <label htmlFor={`${id}-contact-email`} className={labelCls}>
          Contact email
        </label>
        <input
          id={`${id}-contact-email`}
          type="email"
          value={draft.contact_email}
          onChange={(e) => set('contact_email', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1"
        />
      </div>
      <div>
        <label htmlFor={`${id}-contact-phone`} className={labelCls}>
          Contact phone
        </label>
        <input
          id={`${id}-contact-phone`}
          value={draft.contact_phone}
          onChange={(e) => set('contact_phone', e.target.value)}
          className="arbi-input w-full text-xs px-2 py-1"
        />
      </div>
      <div className="col-span-2">
        <label htmlFor={`${id}-notes`} className={labelCls}>
          Notes
        </label>
        <textarea
          id={`${id}-notes`}
          value={draft.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={2}
          className="arbi-input w-full text-xs px-2 py-1"
        />
      </div>
      <div className="col-span-2">
        <button
          type="button"
          onClick={() => set('is_active', !draft.is_active)}
          aria-pressed={draft.is_active}
          className={clsx(
            'text-2xs px-2 py-0.5 rounded flex items-center gap-1',
            draft.is_active ? 'bg-success/15 text-success' : 'bg-surface-4 text-ink-muted'
          )}
        >
          <Check size={10} /> {draft.is_active ? 'Active' : 'Inactive'}
        </button>
      </div>
    </div>
  );
}

function WarehousesSection({ sectionRefs, showToast, isAdmin }: SectionProps): JSX.Element {
  // Day 64 A.7: widen Add + Edit gates from admin-only to admin+user (matches
  // warehouses_insert_admin_user + warehouses_update_admin_user RLS policies).
  // Delete stays admin-only (matches the new warehouses_delete_admin policy
  // added by Phase 1; per "never-delete-only-deactivate" architecture rule).
  const { role } = useApp();
  const canEdit = role === 'admin' || role === 'user';
  const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newDraft, setNewDraft] = useState<WarehouseDraft>(EMPTY_WAREHOUSE_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<WarehouseDraft>(EMPTY_WAREHOUSE_DRAFT);
  const [confirmDelete, setConfirmDelete] = useState<WarehouseRow | null>(null);
  // Day 84.6 P5 — server-side field errors from translatePostgrestError.
  const [serverFieldErrors, setServerFieldErrors] = useState<UserError[]>([]);

  const load = useCallback(async (): Promise<void> => {
    const { data } = await supabase.from('warehouses').select('*').order('name');
    setWarehouses((data as unknown as WarehouseRow[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAdd(): Promise<void> {
    const payload = buildWarehousePayload(newDraft);
    if ('error' in payload) {
      showToast({ kind: 'error', message: String(payload.error) });
      return;
    }
    setServerFieldErrors([]);
    const { error } = await supabase.from('warehouses').insert(payload as never);
    if (error) {
      const ue = translatePostgrestError(error);
      if (ue.field_path !== null) {
        setServerFieldErrors([ue]);
      } else {
        showToast({ kind: 'error', message: `Save failed - ${ue.message}` });
      }
      return;
    }
    showToast({ kind: 'success', message: `${payload.name as string} added` });
    setShowAddForm(false);
    setNewDraft(EMPTY_WAREHOUSE_DRAFT);
    await load();
  }

  function beginEdit(w: WarehouseRow): void {
    setEditingId(w.id);
    setEditDraft(warehouseToDraft(w));
  }

  async function saveEdit(w: WarehouseRow): Promise<void> {
    const payload = buildWarehousePayload(editDraft);
    if ('error' in payload) {
      showToast({ kind: 'error', message: String(payload.error) });
      return;
    }
    setServerFieldErrors([]);
    const { error } = await supabase
      .from('warehouses')
      .update(payload as never)
      .eq('id', w.id);
    if (error) {
      const ue = translatePostgrestError(error);
      if (ue.field_path !== null) {
        setServerFieldErrors([ue]);
      } else {
        showToast({ kind: 'error', message: `Save failed - ${ue.message}` });
      }
      return;
    }
    showToast({ kind: 'success', message: `${payload.name as string} updated` });
    setEditingId(null);
    await load();
  }

  async function handleDelete(w: WarehouseRow): Promise<void> {
    const { error } = await supabase.from('warehouses').delete().eq('id', w.id);
    if (error) {
      showToast({
        kind: 'error',
        message: `Delete failed - ${translatePostgrestError(error).message}`,
      });
    } else {
      showToast({ kind: 'success', message: `${w.name ?? 'Warehouse'} removed` });
      await load();
    }
    setConfirmDelete(null);
  }

  const addAction = canEdit ? (
    <button
      onClick={() => {
        setShowAddForm((v) => !v);
        setNewDraft(EMPTY_WAREHOUSE_DRAFT);
      }}
      className="arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-2 py-0.5 flex items-center gap-1"
    >
      <Plus size={10} /> {showAddForm ? 'Cancel' : 'Add Warehouse'}
    </button>
  ) : null;

  return (
    <SectionCard id="warehouses" title="Warehouses" sectionRefs={sectionRefs} action={addAction}>
      {showAddForm && canEdit && (
        <div className="mb-4 bg-surface-2 border border-accent/30 rounded-lg p-3 space-y-3">
          <WarehouseForm
            draft={newDraft}
            onChange={setNewDraft}
            serverFieldErrors={serverFieldErrors}
          />
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewDraft(EMPTY_WAREHOUSE_DRAFT);
              }}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright px-3 py-1"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1"
            >
              Save Warehouse
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-ink-muted">Loading...</p>
      ) : warehouses.length === 0 ? (
        <div className="py-8 text-center">
          <Warehouse size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">
            No warehouses yet. Add one to anchor inventory and reservations.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-border-subtle">
          {warehouses.map((w) => {
            const isEditing = editingId === w.id;
            return (
              <div key={w.id} className="py-3 px-1">
                {isEditing && canEdit ? (
                  <div className="space-y-3">
                    <WarehouseForm
                      draft={editDraft}
                      onChange={setEditDraft}
                      serverFieldErrors={serverFieldErrors}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingId(null)}
                        className="arbi-btn text-xs bg-surface-4 text-ink-secondary px-3 py-1"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => saveEdit(w)}
                        className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1 flex items-center gap-1"
                      >
                        <Save size={10} /> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-sm text-ink-primary font-medium">{w.name}</span>
                      <span className="text-xs font-mono text-ink-muted">{w.code}</span>
                      {w.address && (
                        <span className="text-xs text-ink-muted truncate">{w.address}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-ink-secondary">
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-2xs font-medium',
                          w.is_active ? 'bg-success/15 text-success' : 'bg-surface-3 text-ink-muted'
                        )}
                      >
                        {w.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {canEdit && (
                        <button
                          onClick={() => beginEdit(w)}
                          className="text-ink-muted hover:text-ink-bright flex items-center gap-1"
                          aria-label={`Edit ${w.name ?? 'warehouse'}`}
                        >
                          <Pencil size={10} /> Edit
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => setConfirmDelete(w)}
                          className="text-ink-muted hover:text-danger flex items-center gap-1"
                          aria-label={`Delete ${w.name ?? 'warehouse'}`}
                        >
                          <Trash2 size={10} /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove Warehouse"
        message={
          confirmDelete
            ? `${confirmDelete.name ?? 'Warehouse'} will be removed. Existing inventory_reservations.warehouse_id RESTRICTs delete; deactivate via the toggle if currently linked.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </SectionCard>
  );
}

// ── Main Directory page ───────────────────────────────────────────────────

export default function Directory(): JSX.Element {
  const { isAdmin } = useApp();
  const location = useLocation();
  const [toast, setToast] = useState<ToastState | null>(null);
  const [activeSection, setActiveSection] = useState<SectionId>('distributors');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const showToast = useCallback((t: ToastState) => setToast(t), []);

  // Day 83 P6.2 — manual_override_stale_days resolved once at parent level,
  // passed down via SectionProps. 90-day fallback if param not yet loaded.
  // Business-level param per Day 82 §2 seed; Directory is distributor-agnostic
  // so we resolve at business level only (no distributor context).
  const paramStore = useParamStore();
  const staleParam = paramStore.resolveKey('manual_override_stale_days');
  const staleParsed = Number(staleParam?.param.value);
  const staleDays: number = Number.isFinite(staleParsed) ? staleParsed : 90;

  // Determine active section from URL hash or path
  useEffect(() => {
    const path = location.pathname.split('/').pop();
    const navItem = NAV_ITEMS.find((n) => n.id === path);
    if (navItem) setActiveSection(navItem.id);
  }, [location.pathname]);

  function scrollToSection(id: SectionId): void {
    setActiveSection(id);
    const el = sectionRefs.current[id] || document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="h-full overflow-auto">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <div className="max-w-5xl mx-auto p-5">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-ink-bright">Directory</h1>
          <p className="text-sm text-ink-muted mt-0.5">
            Manage distributors, manufacturers, warehouses, brands, categories, and marketplace
            accounts
          </p>
        </div>

        <div className="flex gap-6">
          {/* Left panel nav */}
          <nav className="w-48 flex-shrink-0">
            <div className="sticky top-5 space-y-0.5">
              {NAV_ITEMS.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors text-left',
                      isActive
                        ? 'border-l-2 border-accent text-accent bg-accent/10'
                        : 'text-ink-secondary hover:text-ink-primary hover:bg-surface-3'
                    )}
                  >
                    <item.icon size={14} className={isActive ? 'text-accent' : 'text-ink-muted'} />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-6 pb-12">
            <DistributorsSection
              sectionRefs={sectionRefs}
              showToast={showToast}
              isAdmin={isAdmin}
              staleDays={staleDays}
            />
            <ManufacturersSection
              sectionRefs={sectionRefs}
              showToast={showToast}
              isAdmin={isAdmin}
              staleDays={staleDays}
            />
            <WarehousesSection
              sectionRefs={sectionRefs}
              showToast={showToast}
              isAdmin={isAdmin}
              staleDays={staleDays}
            />
            <BrandsSection
              sectionRefs={sectionRefs}
              showToast={showToast}
              isAdmin={isAdmin}
              staleDays={staleDays}
            />
            <CategoriesSection
              sectionRefs={sectionRefs}
              showToast={showToast}
              isAdmin={isAdmin}
              staleDays={staleDays}
            />
            <MarketplacesSection
              sectionRefs={sectionRefs}
              showToast={showToast}
              isAdmin={isAdmin}
              staleDays={staleDays}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
